/* ==========================================================================
   Stacks — name pen holder studio
   Type a name → it is laid out, fitted to the bed and built as the real part in 3D.
   Pick a style, a rhythm, colours and cups → add to cart with the design on the line.
   The maths is name-holder-gen.js (shared with the maker view and the unit tests), the
   event log is name-holder-log.js. three.js and opentype.js load when the studio is on
   screen; until then — and on devices without WebGL — the name shows as a flat preview
   in its real font, and ordering still works.
   ========================================================================== */
import * as GEN from 'name-holder-gen';
import { createLog } from 'name-holder-log';

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* English defaults; the section passes the store's translations over them. */
const EN = {
  solid_make:'Thick 3D letters with spacious open compartments behind them for pens and desk tools. Closed bottoms and reinforced walls. Finished before dispatch.',
  err_model:'This design could not be built safely. Try another name or style.',
  step_name: 'Your name', name_label: 'Name on the holder', clear: 'Clear', symbols: 'Add a symbol', chars: '{n}/{max}',
  step_style: 'Style', step_shape: 'Shape', step_layout: 'Letter layout', layout_straight: 'Straight', layout_bounce: 'Bounce', layout_wave: 'Wave', layout_arch: 'Arch',
  case_label: 'Capitals', case_upper: 'ABC', case_title: 'Abc', case_asis: 'As typed', lines_label: 'Lines', lines_1: 'One', lines_2: 'Two',
  step_color: 'Colours', tone_one: 'One colour', tone_two: 'Two colours', slot_letters: 'Letters', slot_base: 'Base', slot_name: 'Name', slot_body: 'Holder', tone_base: 'Colour base', tone_kit: 'Colour letters', note_base: 'The name stands on a band in the second colour.', note_kit: 'The letters print on their own, face up, and key onto the holder with hidden pins — sharper, and 4 mm deep.', duos: 'Pairs we love',
  finish_matte: 'Matte', finish_basic: 'Glossy', finish_silk: 'Silk',
  step_pockets: 'Room inside', pockets_mixed: 'Regular', pockets_pens: 'Slim', pockets_chunky: 'Wide', cups_count: '{n} cups', inside: '{n} cm inside',
  size_0: 'Small', size_1: 'Medium', size_2: 'Large', tier_range: '{a}–{b} characters',
  summary: '{w} × {d} × {h} cm · {g} g', add: 'Add to cart', buy: 'Buy now', adding: 'Adding…', added: 'In your cart',
  share: 'Share', copied: 'Link copied', surprise: 'Surprise me', pens: 'Pens', reset_view: 'Front view', drag_hint: 'Drag to turn · pinch to zoom',
  loading: 'Warming up the printer…', webgl_off: 'The 3D preview needs WebGL, which this browser has turned off. Your design is still built exactly as shown.',
  err_fonts: 'The font did not load. Check your connection and try again.', retry: 'Try again',
  err_empty: 'Type a name to start.', err_unsupported: '“{chars}” is not in this style. Remove it or try another style.',
  err_too_long: 'Too long for one holder. Shorten it, or try the Tall style.', warn_small: 'A long one — letters print {cap} mm tall.',
  err_sold_out: 'Sold out for now.', err_cart: 'Could not add it. Try again.', err_limit: '{max} characters at most.',
  no_supports: 'One piece, printed upright — no supports, no sanding.', no_supports_kit: 'Holder and letters print separately, no supports — the letters press onto hidden pins.', no_supports_sign: 'One piece, printed flat — no supports, no sanding.', lead: '',
  sym_more: 'More',
  maker_title: 'Maker', maker_all: 'STL · whole part', maker_body: 'STL · base', maker_letters: 'STL · letters', maker_code: 'Design code', maker_load: 'Load',
  maker_notes: 'Upright on the bed · no supports · 0.2 mm layers · 3 walls · 15% gyroid. Two colours: load both STLs as parts of one object.',
  solid_maker_notes: 'Upright · 0.2 mm layers · 4 walls · 15% infill · normal supports as needed. Two colours: one filament change at Z 5 mm. Inspect the sliced layers and supports before printing.',
  prop_name: 'Name', prop_style: 'Style', prop_colors: 'Colours', prop_design: 'Design',
  aria_preview: '3D preview of “{text}” in {style}, {color}',
  examples: 'SARA|Omar|Boss ★|Nour ♥ Ali|Dr. Mona|TEAM ✦ 7', ghost: 'Sara ♥ Ali',
  cm: 'cm', view_label: 'Preview controls',
  kind_label: "What are you making?", kind_holder: "Pen holder", kind_holder_sub: "Stands on the desk, pens inside", kind_sign: "Wall sign", kind_sign_sub: "One face, hangs flat on the wall", step_hang: "Hanging", hang_screws: "Hidden keyholes", hang_tape: "Flat back", hang_two: "Two screws {s} cm apart, {t} cm below the top — it hangs level.", hang_one: "One screw in the middle, {t} cm below the top.", hang_tape_note: "A smooth back for double-sided mounting tape — no drilling.", hang_fallback: "This shape has no room for keyholes, so it comes with a flat back for tape.", view_back: "Back", title_sign: "Your name, as a wall sign", scene_label: "Where to see it", scene_studio: "Studio", scene_desk: "On a desk", scene_wall: "On a wall", scale_desk: "Life-size: the mug is 9.5 cm tall, the notebook A5", scale_wall: "Life-size: the frame is A4, the switch 8.6 cm", look_label: "Room look", look_oak: "Wood desk", look_setup: "PC setup", look_study: "White desk", look_paint: "Light wall", look_shelf: "Shelf", look_bedroom: "Bedroom", scale_oak: "Life-size: the mug is 9.5 cm tall, the notebook A5", scale_setup: "Life-size: a 27-inch screen and a 36 cm keyboard", scale_study: "Life-size: a 13-inch laptop", scale_paint: "Life-size: the frame is A4, the switch 8.6 cm", scale_shelf: "Life-size: the shelf is 70 cm wide", scale_bedroom: "Life-size: above a 160 cm bed", lead_sign: "Type any name — Arabic or English — and watch it become a sign for your wall. One piece, printed flat, hung on hidden keyholes.", prop_kind: "Type", prop_hang: "Hanging", dims: 'Sizes', dim_w: 'Width', dim_d: 'Thickness', dim_h: 'Height',
  drag_hint_desk: 'Drag to turn · ctrl + scroll to zoom', zoom_hint: 'Hold ctrl (⌘) and scroll to zoom', view_hero: 'Angle', view_front: 'Front', view_top: 'Top', example: 'Example — type your name', colors_all: 'All {n} colours', colors_less: 'Fewer colours',
};
const SYMBOL_NAMES = {
  heart: ['Heart', 'قلب'], star: ['Star', 'نجمة'], skull: ['Skull', 'جمجمة'], sparkle: ['Sparkle', 'لمعة'], bolt: ['Bolt', 'برق'], moon: ['Moon', 'هلال'], sun: ['Sun', 'شمس'],
  flower: ['Flower', 'وردة'], paw: ['Paw', 'رجل قطة'], crown: ['Crown', 'تاج'], note: ['Music', 'نوتة'], smile: ['Smile', 'ضحكة'], cloud: ['Cloud', 'سحابة'],
  infinity: ['Infinity', 'لانهاية'], check: ['Tick', 'صح'], flame: ['Flame', 'نار'], coffee: ['Coffee', 'قهوة'], pencil: ['Pencil', 'قلم'], diamond: ['Diamond', 'ماسة'],
  clover: ['Clover', 'برسيم'], cat: ['Cat', 'قطة'], dot: ['Dot', 'نقطة'], drop: ['Drop', 'قطرة'], leaf: ['Leaf', 'ورقة شجر'], arrow: ['Arrow', 'سهم'],
  plus: ['Plus', 'زائد'], spade: ['Spade', 'بستوني'], balloon: ['Balloon', 'بالونة'], gift: ['Gift', 'هدية'], house: ['Home', 'بيت'], key: ['Key', 'مفتاح'],
  trophy: ['Trophy', 'كأس'], mountain: ['Mountain', 'جبل'], anchor: ['Anchor', 'مرساة'],
};
const EXAMPLES_AR ='نور|سارة|يوسف ♥ مريم|م. أحمد|BOSS ★';

/* "id | English | عربي | #hex | finish" per line */
export function parseColors(raw) {
  return String(raw || '').split(/\r?\n/).map((line) => line.split('|').map((s) => s.trim())).filter((p) => p.length >= 4 && /^#[0-9a-f]{6}$/i.test(p[3]))
    .map(([id, en, ar, hex, finish]) => ({ id: (id || en).toLowerCase().replace(/[^a-z0-9]+/g, '-'), en: en || id, ar: ar || en || id, hex: hex.toUpperCase(), finish: ['matte', 'basic', 'silk'].includes(finish) ? finish : 'basic' }));
}
function money(cents, fmt = '{{amount}}') {
  const n = Number(cents || 0) / 100;
  const fix = (d, th = ',', dec = '.') => { const [i, f] = n.toFixed(d).split('.'); const s = i.replace(/\B(?=(\d{3})+(?!\d))/g, th); return f && !/^0+$/.test(f) ? s + dec + f : s; };
  return String(fmt).replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
    amount: fix(2), amount_no_decimals: fix(0), amount_with_comma_separator: fix(2, '.', ','), amount_no_decimals_with_comma_separator: fix(0, '.', ','),
    amount_with_apostrophe_separator: fix(2, "'"), amount_with_space_separator: fix(2, ' ', ','), amount_no_decimals_with_space_separator: fix(0, ' '),
  }[k] ?? fix(2))).replace(/<[^>]*>/g, '');
}
/* Size × Colours → the variant. Options are found by name (EN or AR), else by position. */
function variantPicker(product) {
  const names = (product.options || []).map((o) => String(o).toLowerCase());
  let size = names.findIndex((n) => /size|مقاس|حجم/.test(n));
  let tone = names.findIndex((n) => /colou?r|tone|لون|ألوان/.test(n));
  if (size < 0 && names.length) size = tone === 0 ? 1 : 0;
  if (tone < 0 && names.length > 1) tone = size === 0 ? 1 : 0;
  const vals = (i) => (i < 0 ? [] : [...new Set(product.variants.map((v) => v.options[i]))]);
  const sizes = vals(size), tones = vals(tone);
  const pick = (tier, t) => product.variants.find((v) => (size < 0 || v.options[size] === sizes[Math.min(tier, sizes.length - 1)])
    && (tone < 0 || v.options[tone] === tones[Math.min(t !== 'one' ? 1 : 0, tones.length - 1)])) || product.variants[0];
  return { pick, hasTone: tones.length > 1 };
}
async function sha256(s) {
  const b = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return [...new Uint8Array(b)].map((x) => x.toString(16).padStart(2, '0')).join('');
}
function download(buf, name) {
  const url = URL.createObjectURL(new Blob([buf], { type: 'model/stl' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
const ICON = {
  straight: '<path d="M3 13h4v-6H3zM10 13h4v-6h-4zM17 13h4v-6h-4z"/>',
  bounce: '<path d="M3 11h4V5H3zM10 15h4V9h-4zM17 11h4V5h-4z"/>',
  wave: '<path d="M2 12c2.5-5 5-5 7.5 0s5 5 7.5 0 3.5-3 5-2"/>',
  arch: '<path d="M3 16c3-8 15-8 18 0"/>',
  dice: '<rect x="4" y="4" width="16" height="16" rx="4"/><circle cx="9" cy="9" r="1.1"/><circle cx="15" cy="15" r="1.1"/><circle cx="15" cy="9" r="1.1"/><circle cx="9" cy="15" r="1.1"/>',
  pen: '<path d="M4 20l1-4L16 5l3 3L8 19z"/><path d="M14 7l3 3"/>',
  front: '<path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3"/><circle cx="12" cy="12" r="3"/>',
  share: '<path d="M12 3v12M7 8l5-5 5 5"/><path d="M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6"/>',
  mixed: '<rect x="3" y="8" width="18" height="8" rx="3"/>',
  pens: '<rect x="3" y="9.5" width="18" height="5" rx="2"/>',
  chunky: '<rect x="3" y="6" width="18" height="12" rx="4"/>',
  check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>',
  hang_screws: '<circle cx="12" cy="9" r="4"/><path d="M12 13v7"/>',
  hang_tape: '<rect x="4" y="8" width="16" height="8" rx="2"/><path d="M8 8v8M16 8v8"/>',
  ruler: '<path d="M3.5 16.5 16.5 3.5l4 4-13 13z"/><path d="m7.5 12.5 2 2M10.5 9.5l1.5 1.5M13.5 6.5l2 2"/>',
};
const svg = (d, size = 20) => `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;

/* ---- fonts for the flat previews (style cards, the first paint, the no-WebGL view) ---- */
const LATIN_RANGE = 'U+0000-024F,U+1E00-1EFF,U+2000-206F,U+20A0-20CF,U+2100-214F';
const ARABIC_RANGE = 'U+0600-06FF,U+0750-077F,U+08A0-08FF,U+FB50-FDFF,U+FE70-FEFF';
const cssFonts = new Map();
function cssFont(style) {
  if (cssFonts.has(style.id)) return cssFonts.get(style.id);
  const files = GEN.fontFilesOf(style);
  const family = `nh-${style.id}`;
  const faces = [
    new FontFace(family, `url(${files.latin[0]}) format("woff")`, { unicodeRange: LATIN_RANGE, display: 'swap' }),
    new FontFace(family, `url(${files.arabic[0]}) format("woff")`, { unicodeRange: ARABIC_RANGE, display: 'swap' }),
  ];
  const p = Promise.allSettled(faces.map((f) => f.load().then((ff) => document.fonts.add(ff)))).then(() => family);
  cssFonts.set(style.id, p);
  return p;
}

/* ==========================================================================
   The 3D stage
   ========================================================================== */
async function createStage(root, { onInteract, onWheelPass } = {}) {
  const THREE = await import('three');
  const [{ OrbitControls }, { RoomEnvironment }, { RoundedBoxGeometry }] = await Promise.all([
    import('three/addons/controls/OrbitControls.js'),
    import('three/addons/environments/RoomEnvironment.js'),
    import('three/addons/geometries/RoundedBoxGeometry.js'),
  ]);
  const host = $('[data-nh-canvas]', root);
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (e) { const err = new Error('webgl'); err.cause = e; throw err; }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.localClippingEnabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  host.appendChild(renderer.domElement);
  renderer.domElement.setAttribute('role', 'img');

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  /* a product-photo light: soft room for colour, one strong key from high left for form */
  scene.environmentIntensity = 0.55;
  const camera = new THREE.PerspectiveCamera(27, 1, 2, 6000);
  const key = new THREE.DirectionalLight(0xffffff, 3.2);
  /* a little lower than a ceiling light, and a crisp shadow: each letter draws a thin shade line
     on the face beside it — on one-colour parts that line is what makes the name legible */
  key.position.set(-300, 300, 330);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.radius = 2.5;
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.25;
  Object.assign(key.shadow.camera, { left: -200, right: 200, top: 200, bottom: -200, near: 10, far: 1200 });
  const hemi = new THREE.HemisphereLight(0xffffff, 0xe9e4d8, 0.55);
  scene.add(key, key.target, hemi);
  const rim = new THREE.DirectionalLight(0xfff4e0, 0.7); rim.position.set(220, 140, -260); scene.add(rim);

  /* the desk: only shadows land on it, the page shows through */
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(4000, 4000), new THREE.ShadowMaterial({ opacity: 0.16 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);
  /* the wall a sign hangs on: like the desk, only its shadow shows */
  const wall = new THREE.Mesh(new THREE.PlaneGeometry(4000, 4000), new THREE.ShadowMaterial({ opacity: 0.14 }));
  wall.receiveShadow = true; wall.visible = false; scene.add(wall);
  const blob = (() => {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const g = c.getContext('2d'); const grd = g.createRadialGradient(64, 64, 4, 64, 64, 64);
    grd.addColorStop(0, 'rgba(15,16,19,.34)'); grd.addColorStop(1, 'rgba(15,16,19,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(c), transparent: true, depthWrite: false }));
    m.rotation.x = -Math.PI / 2; m.position.y = 0.05; scene.add(m); return m;
  })();

  /* ---- real rooms: the part on a desk or on a wall, among things everyone knows the size of ----
     All in millimetres, true to life, so the customer judges the size by eye:
       a standard mug 82 × 95, an A5 notebook 148 × 210, a pencil 175 long,
       an A4 frame 230 × 320, a wall switch plate 86 × 86. Textures are painted here, no downloads. */
  const room = new THREE.Group(); room.visible = false; scene.add(room);
  let sceneName = 'studio';
  const seeded = (s) => () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  const paintTex = (w, h, paint, rep = [1, 1]) => {
    const c = document.createElement('canvas'); c.width = w; c.height = h; paint(c.getContext('2d'), w, h);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(...rep); t.anisotropy = 8; return t;
  };
  let roomMats = null;
  const mats = () => roomMats || (roomMats = {
    /* oak: long soft grain, a few darker streaks */
    wood: new THREE.MeshStandardMaterial({ roughness: 0.55, map: paintTex(1024, 512, (g, w, h) => {
      const r = seeded(7); g.fillStyle = '#B8895C'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 260; i++) {
        const y = r() * h, a = 0.04 + r() * 0.08, dark = r() < 0.5;
        g.strokeStyle = dark ? `rgba(92,58,30,${a})` : `rgba(235,200,160,${a * 0.8})`; g.lineWidth = 1 + r() * 3;
        g.beginPath(); for (let x = 0; x <= w; x += 16) g.lineTo(x, y + Math.sin(x / (140 + r() * 90) + i) * (0.8 + r() * 1.8)); g.stroke();   // long, gentle grain
      }
    }, [2, 2]) }),
    /* matt emulsion with the faint grain of a roller */
    paint: new THREE.MeshStandardMaterial({ roughness: 0.95, map: paintTex(512, 512, (g, w, h) => {
      const r = seeded(11); g.fillStyle = '#E9E3D8'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 9000; i++) { g.fillStyle = `rgba(${r() < 0.5 ? '255,255,255' : '120,110,95'},${0.035 + r() * 0.04})`; g.fillRect(r() * w, r() * h, 1 + r() * 2, 1 + r() * 2); }
    }, [5, 5]) }),
    ceramic: new THREE.MeshPhysicalMaterial({ color: 0x8FA58E, roughness: 0.3, clearcoat: 0.7, clearcoatRoughness: 0.2 }),
    coffee: new THREE.MeshStandardMaterial({ color: 0x3A2215, roughness: 0.25 }),
    cover: new THREE.MeshStandardMaterial({ color: 0x2E4638, roughness: 0.7 }),
    pages: new THREE.MeshStandardMaterial({ color: 0xF1ECE2, roughness: 0.9 }),
    pencil: new THREE.MeshStandardMaterial({ color: 0xF2B632, roughness: 0.5 }),
    bare: new THREE.MeshStandardMaterial({ color: 0xE7C89B, roughness: 0.8 }),
    lead: new THREE.MeshStandardMaterial({ color: 0x2A2A2E, roughness: 0.6 }),
    frame: new THREE.MeshStandardMaterial({ color: 0x1D1D1F, roughness: 0.45 }),
    mount: new THREE.MeshStandardMaterial({ color: 0xF6F3EC, roughness: 0.9 }),
    art: new THREE.MeshStandardMaterial({ roughness: 0.8, map: paintTex(460, 640, (g, w, h) => {
      const grd = g.createLinearGradient(0, 0, 0, h); grd.addColorStop(0, '#E8D3B6'); grd.addColorStop(1, '#C98F6A'); g.fillStyle = grd; g.fillRect(0, 0, w, h);
      g.fillStyle = '#D2F24B'; g.beginPath(); g.arc(w * 0.62, h * 0.36, w * 0.2, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#1F5B5C'; g.beginPath(); g.moveTo(0, h * 0.78); for (let x = 0; x <= w; x += 20) g.lineTo(x, h * 0.72 + Math.sin(x / 55) * 22); g.lineTo(w, h); g.lineTo(0, h); g.fill();
    }) }),
    plate: new THREE.MeshPhysicalMaterial({ color: 0xF7F6F2, roughness: 0.35, clearcoat: 0.3 }),
  });
  const put = (geo, mat, x, y, z, ry = 0) => { const m = new THREE.Mesh(geo, mat); m.position.set(x, y, z); m.rotation.y = ry; m.castShadow = m.receiveShadow = true; room.add(m); return m; };
  /* more rooms: each kind of room comes in three looks — all life-size, all painted here */
  const ROOMS = { desk: ['oak', 'setup', 'study'], wall: ['paint', 'shelf', 'bedroom'] };
  let roomVariant = 0;
  let room2 = null;
  const mats2 = () => room2 || (room2 = {
    walnut: new THREE.MeshStandardMaterial({ roughness: 0.5, map: paintTex(1024, 512, (g, w, h) => {
      const r = seeded(17); g.fillStyle = '#5B3F2C'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 240; i++) {
        const y = r() * h, a = 0.05 + r() * 0.1;
        g.strokeStyle = r() < 0.6 ? `rgba(38,24,14,${a})` : `rgba(150,105,70,${a * 0.7})`; g.lineWidth = 1 + r() * 3;
        g.beginPath(); for (let x = 0; x <= w; x += 16) g.lineTo(x, y + Math.sin(x / (150 + r() * 90) + i) * (0.8 + r() * 1.6)); g.stroke();
      }
    }, [2, 2]) }),
    /* a felt desk mat: fine speckle */
    felt: new THREE.MeshStandardMaterial({ roughness: 1, map: paintTex(512, 512, (g, w, h) => {
      const r = seeded(23); g.fillStyle = '#2E3035'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 14000; i++) { g.fillStyle = `rgba(${r() < 0.5 ? '255,255,255' : '0,0,0'},${0.03 + r() * 0.04})`; g.fillRect(r() * w, r() * h, 1, 1); }
    }, [3, 2]) }),
    darkPaint: new THREE.MeshStandardMaterial({ roughness: 0.95, color: 0x3A3D45, map: paintTex(512, 512, (g, w, h) => {
      const r = seeded(29); g.fillStyle = '#FFFFFF'; g.fillRect(0, 0, w, h);
      for (let i = 0; i < 9000; i++) { g.fillStyle = `rgba(${r() < 0.5 ? '255,255,255' : '0,0,0'},${0.04 + r() * 0.05})`; g.fillRect(r() * w, r() * h, 1 + r() * 2, 1 + r() * 2); }
    }, [5, 5]) }),
    olivePaint: new THREE.MeshStandardMaterial({ roughness: 0.95, color: 0x55654F }),
    blushPaint: new THREE.MeshStandardMaterial({ roughness: 0.95, color: 0xE9D6CB }),
    sagePaint: new THREE.MeshStandardMaterial({ roughness: 0.95, color: 0xDCE0D6 }),
    whiteTop: new THREE.MeshStandardMaterial({ roughness: 0.6, color: 0xF1EFEA }),
    black: new THREE.MeshStandardMaterial({ roughness: 0.45, color: 0x18191C }),
    graphite: new THREE.MeshStandardMaterial({ roughness: 0.55, color: 0x2A2C31 }),
    keycap: new THREE.MeshStandardMaterial({ roughness: 0.6, color: 0x33363D }),
    keyAccent: new THREE.MeshStandardMaterial({ roughness: 0.6, color: 0xD2F24B }),
    alu: new THREE.MeshStandardMaterial({ roughness: 0.32, metalness: 0.65, color: 0xC7CAD0 }),
    potWhite: new THREE.MeshPhysicalMaterial({ roughness: 0.4, color: 0xF3F1EC, clearcoat: 0.3 }),
    potClay: new THREE.MeshStandardMaterial({ roughness: 0.85, color: 0xC27B55 }),
    soil: new THREE.MeshStandardMaterial({ roughness: 1, color: 0x3B2A1E }),
    leaf: new THREE.MeshStandardMaterial({ roughness: 0.55, color: 0x3F6B3A, side: THREE.DoubleSide }),
    leafLight: new THREE.MeshStandardMaterial({ roughness: 0.55, color: 0x6E9A4E, side: THREE.DoubleSide }),
    oak: new THREE.MeshStandardMaterial({ roughness: 0.55, color: 0xC9A27A }),
    fabric: new THREE.MeshStandardMaterial({ roughness: 1, color: 0xCDBFAE }),
    linen: new THREE.MeshStandardMaterial({ roughness: 1, color: 0xF4F1EA }),
    linenGrey: new THREE.MeshStandardMaterial({ roughness: 1, color: 0xB9B7B1 }),
    shade: new THREE.MeshStandardMaterial({ roughness: 0.9, color: 0xF3EDE2, emissive: 0xFFE2B0, emissiveIntensity: 0.35, side: THREE.DoubleSide }),
    books: [0xB5553A, 0xE9E1CF, 0x2F3E57, 0x7E8C6A, 0xD9A441].map((c) => new THREE.MeshStandardMaterial({ roughness: 0.8, color: c })),
    /* a desktop wallpaper: deep blue into violet, two soft glows */
    screen: new THREE.MeshBasicMaterial({ map: paintTex(1024, 608, (g, w, h) => {
      const grd = g.createLinearGradient(0, 0, w, h); grd.addColorStop(0, '#141A3A'); grd.addColorStop(0.55, '#3B2A73'); grd.addColorStop(1, '#101222');
      g.fillStyle = grd; g.fillRect(0, 0, w, h);
      for (const [x, y, rr, c] of [[0.7, 0.35, 0.45, 'rgba(210,242,75,.35)'], [0.25, 0.75, 0.5, 'rgba(90,140,255,.35)']]) {
        const rg = g.createRadialGradient(x * w, y * h, 0, x * w, y * h, rr * w); rg.addColorStop(0, c); rg.addColorStop(1, 'rgba(0,0,0,0)'); g.fillStyle = rg; g.fillRect(0, 0, w, h);
      }
      g.fillStyle = 'rgba(255,255,255,.12)'; g.fillRect(0, h - 26, w, 26);   // a task bar
    }) }),
    /* a laptop's light screen: a document open */
    laptopScreen: new THREE.MeshBasicMaterial({ map: paintTex(640, 400, (g, w, h) => {
      g.fillStyle = '#F4F4F1'; g.fillRect(0, 0, w, h); g.fillStyle = '#E3E3DE'; g.fillRect(0, 0, w, 26);
      g.fillStyle = '#C9CBC4'; for (let i = 0; i < 9; i++) g.fillRect(120, 60 + i * 30, 400 - (i % 3) * 70, 10);
      g.fillStyle = '#D2F24B'; g.fillRect(120, 42, 160, 8);
    }) }),
    /* the glow a light strip throws on the wall behind a screen */
    glow: new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, map: paintTex(512, 256, (g, w, h) => {
      const rg = g.createRadialGradient(w / 2, h / 2, 10, w / 2, h / 2, w / 2); rg.addColorStop(0, 'rgba(140,110,255,.55)'); rg.addColorStop(0.5, 'rgba(90,120,255,.22)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = rg; g.fillRect(0, 0, w, h);
    }) }),
  });
  const box = (sx, sy, sz, mat, x, y, z, ry = 0) => put(new THREE.BoxGeometry(sx, sy, sz), mat, x, y, z, ry);
  const rbox = (sx, sy, sz, r, mat, x, y, z, ry = 0) => put(new RoundedBoxGeometry(sx, sy, sz, 4, Math.min(r, sx / 2, sy / 2, sz / 2)), mat, x, y, z, ry);
  /* a snake plant: blades standing out of a pot */
  function snakePlant(x, z, s = 1) {
    const M = mats2(), g = new THREE.Group(); g.position.set(x, 0, z); room.add(g);
    const pot = new THREE.Mesh(new THREE.CylinderGeometry(48 * s, 38 * s, 95 * s, 40), M.potWhite); pot.position.y = 47.5 * s; pot.castShadow = pot.receiveShadow = true; g.add(pot);
    const soil = new THREE.Mesh(new THREE.CircleGeometry(45 * s, 32), M.soil); soil.rotation.x = -Math.PI / 2; soil.position.y = 90 * s; g.add(soil);
    const r = seeded(31);
    for (let i = 0; i < 8; i++) {
      const hgt = (170 + r() * 130) * s, blade = new THREE.Mesh(new THREE.ConeGeometry(14 * s, hgt, 4, 1, true), i % 3 ? M.leaf : M.leafLight);
      blade.scale.z = 0.18; const a = (i / 8) * Math.PI * 2;
      blade.position.set(Math.cos(a) * 18 * s, 90 * s + hgt / 2, Math.sin(a) * 18 * s);
      blade.rotation.set(Math.sin(a) * 0.14, a, -Math.cos(a) * 0.14); blade.castShadow = true; g.add(blade);
    }
    return g;
  }
  /* a trailing plant in a small pot: round leaves */
  function roundPlant(x, y, z, s = 1) {
    const M = mats2(), g = new THREE.Group(); g.position.set(x, y, z); room.add(g);
    const pot = new THREE.Mesh(new THREE.CylinderGeometry(42 * s, 34 * s, 80 * s, 32), M.potClay); pot.position.y = 40 * s; pot.castShadow = pot.receiveShadow = true; g.add(pot);
    const r = seeded(37);
    for (let i = 0; i < 26; i++) {
      const lf = new THREE.Mesh(new THREE.SphereGeometry(16 * s, 12, 8), r() < 0.5 ? M.leaf : M.leafLight); lf.scale.set(1, 0.35, 0.8);
      const a = r() * Math.PI * 2, rad = (10 + r() * 48) * s, drop = r() < 0.35 ? -r() * 70 * s : r() * 40 * s;
      lf.position.set(Math.cos(a) * rad, 82 * s + drop, Math.sin(a) * rad * 0.7); lf.rotation.set(r() - 0.5, r() * 3, r() - 0.5); lf.castShadow = true; g.add(lf);
    }
    return g;
  }
  function buildRoom() {
    for (const o of [...room.children]) { room.remove(o); o.traverse((c) => c.geometry?.dispose()); }
    if (sceneName === 'studio' || !plan) return;
    const M = mats(), N = mats2(), { w, d, h } = plan.size;
    const look = ROOMS[sceneName][roomVariant % ROOMS[sceneName].length];
    if (look === 'oak') {
      /* the desk top, and the wall it stands against */
      const top = put(new THREE.PlaneGeometry(2400, 1400), M.wood, 0, 0, 0); top.rotation.x = -Math.PI / 2; top.castShadow = false;
      const back = put(new THREE.PlaneGeometry(4000, 2000), M.paint, 0, 1000, -Math.max(260, d / 2 + 190)); back.castShadow = false;
      /* a mug to its right: 82 across, 95 tall, 3 mm walls, a handle and coffee in it */
      const mx = w / 2 + 78, mz = -12;
      const prof = [[0, 0], [36, 0], [40, 2], [41, 10], [41, 93], [39.5, 95], [38, 93], [38, 6], [0, 6]].map(([r, y]) => new THREE.Vector2(r, y));
      put(new THREE.LatheGeometry(prof, 64), M.ceramic, mx, 0, mz);
      const handle = put(new THREE.TorusGeometry(19, 5, 16, 40, Math.PI), M.ceramic, mx + 40, 50, mz); handle.rotation.z = -Math.PI / 2;
      const cup = put(new THREE.CircleGeometry(38, 48), M.coffee, mx, 80, mz); cup.rotation.x = -Math.PI / 2;
      /* an A5 notebook to its left, lying a little askew */
      const nx = -w / 2 - 128, nz = 26, ang = 0.22;
      put(new THREE.BoxGeometry(148, 1.4, 210), M.cover, nx, 0.7, nz, ang);
      put(new THREE.BoxGeometry(144, 9.6, 204), M.pages, nx + 1.5, 6.2, nz, ang);
      put(new THREE.BoxGeometry(148, 1.4, 210), M.cover, nx, 11.7, nz, ang);
      /* a pencil in front, 175 mm, lying on the desk */
      const pencil = new THREE.Group(); pencil.position.set(-w * 0.05, 3.7, d / 2 + 52); pencil.rotation.y = 0.12; room.add(pencil);
      const seg = (geo, mat, x) => { const m = new THREE.Mesh(geo, mat); m.rotation.z = Math.PI / 2; m.position.x = x; m.castShadow = m.receiveShadow = true; pencil.add(m); };
      seg(new THREE.CylinderGeometry(3.6, 3.6, 157, 6), M.pencil, 0);
      seg(new THREE.CylinderGeometry(3.6, 0.9, 15, 6), M.bare, 85.5);
      seg(new THREE.CylinderGeometry(0.9, 0.2, 3, 12), M.lead, 94.5);
    } else if (look === 'setup') {
      /* a walnut desk against a dark wall; a 27" screen with a light strip behind it, a keyboard and a
         mouse on a felt mat — the holder at the left of the keyboard, where it lives on a real desk */
      const wallZ = -Math.max(420, d / 2 + 360);
      const top = put(new THREE.PlaneGeometry(2600, 1400), N.walnut, 0, 0, 0); top.rotation.x = -Math.PI / 2; top.castShadow = false;
      put(new THREE.PlaneGeometry(4000, 2200), N.darkPaint, 0, 1100, wallZ).castShadow = false;
      const kx = w / 2 + 70 + 178;
      rbox(820, 3, 330, 3, N.felt, w / 2 + 35 + 410, 1.5, 10).castShadow = false;   // 82 × 33 cm mat, starting clear of the holder
      /* a tenkeyless keyboard, 355 × 125: a low case and 87 caps on a 19 mm pitch */
      const kb = new THREE.Group(); kb.position.set(kx, 3, 20); kb.rotation.set(0.05, -0.04, 0); room.add(kb);
      const kcase = new THREE.Mesh(new RoundedBoxGeometry(355, 16, 125, 3, 4), N.graphite); kcase.position.y = 8; kcase.castShadow = kcase.receiveShadow = true; kb.add(kcase);
      const rows = [[15, 0], [15, 0], [14, 1], [13, 2], [12, 3]], caps = [];
      rows.forEach(([n, wide], ri) => { for (let i = 0; i < n; i++) caps.push([-160 + i * 19.05 + wide * 4, -44 + ri * 19.05, i === 0 && ri === 0]); });
      const capGeo = new RoundedBoxGeometry(16.5, 8, 16.5, 2, 2);
      const inst = new THREE.InstancedMesh(capGeo, N.keycap, caps.length), mtx = new THREE.Matrix4();
      caps.forEach(([x, z], i) => { mtx.makeTranslation(x, 20, z); inst.setMatrixAt(i, mtx); });
      inst.castShadow = inst.receiveShadow = true; kb.add(inst);
      const space = new THREE.Mesh(new RoundedBoxGeometry(118, 8, 16.5, 2, 2), N.keycap); space.position.set(-40, 20, 51); space.castShadow = true; kb.add(space);
      for (const [x, z] of [[-160, -44], [118, 32]]) { const acc = new THREE.Mesh(new RoundedBoxGeometry(16.5, 8.4, 16.5, 2, 2), N.keyAccent); acc.position.set(x, 20.2, z); kb.add(acc); }
      /* a mouse: 120 × 64, 38 high */
      const mouse = put(new THREE.SphereGeometry(1, 32, 16), N.black, kx + 290, 0, 30); mouse.scale.set(32, 38, 60);
      /* the screen: 615 × 365 panel on a stand, 25 cm behind the keyboard */
      const sx = kx - 20, sz = -Math.max(230, d / 2 + 190);
      rbox(230, 8, 180, 4, N.graphite, sx, 4, sz);
      box(44, 250, 22, N.graphite, sx, 125, sz - 40);
      rbox(622, 372, 14, 4, N.black, sx, 110 + 186, sz - 20);
      put(new THREE.PlaneGeometry(606, 342), N.screen, sx, 110 + 190, sz - 12.9).castShadow = false;
      const gl = put(new THREE.PlaneGeometry(1300, 760), N.glow, sx, 300, wallZ + 2); gl.castShadow = gl.receiveShadow = false;
      snakePlant(-w / 2 - 140, -120, 1);
    } else if (look === 'study') {
      /* a white desk by a sage wall: an open 13" laptop, a stack of books, a mug */
      const top = put(new THREE.PlaneGeometry(2400, 1400), N.whiteTop, 0, 0, 0); top.rotation.x = -Math.PI / 2; top.castShadow = false;
      put(new THREE.PlaneGeometry(4000, 2000), N.sagePaint, 0, 1000, -Math.max(360, d / 2 + 300)).castShadow = false;
      /* laptop: 304 × 212, the lid open at 110° */
      const lp = new THREE.Group(); lp.position.set(w / 2 + 70 + 150, 0, -20); lp.rotation.y = -0.32; room.add(lp);
      const base = new THREE.Mesh(new RoundedBoxGeometry(304, 12, 212, 3, 5), N.alu); base.position.y = 6; base.castShadow = base.receiveShadow = true; lp.add(base);
      const kbd = new THREE.Mesh(new THREE.PlaneGeometry(270, 110), N.graphite); kbd.rotation.x = -Math.PI / 2; kbd.position.set(0, 12.2, -22); lp.add(kbd);
      const hinge = new THREE.Group(); hinge.position.set(0, 12, -106); hinge.rotation.x = -0.35; lp.add(hinge);
      const lid = new THREE.Mesh(new RoundedBoxGeometry(304, 206, 6, 3, 3), N.alu); lid.position.set(0, 103, -3); lid.castShadow = true; hinge.add(lid);
      const scr = new THREE.Mesh(new THREE.PlaneGeometry(286, 184), N.laptopScreen); scr.position.set(0, 104, 0.2); hinge.add(scr);
      /* books to the left, stacked a little askew */
      const bx = -w / 2 - 150;
      [[160, 26, 232, 0.12], [150, 20, 222, -0.06], [142, 24, 210, 0.2]].reduce((y, [bw, bh, bd, ry], i) => { box(bw, bh, bd, N.books[i], bx, y + bh / 2, -40, ry); return y + bh; }, 0);
      roundPlant(bx + 10, 70, -40, 0.8);
      /* a white mug behind, between the books and the laptop */
      const prof = [[0, 0], [36, 0], [40, 2], [41, 10], [41, 93], [39.5, 95], [38, 93], [38, 6], [0, 6]].map(([r, y]) => new THREE.Vector2(r, y));
      put(new THREE.LatheGeometry(prof, 64), N.potWhite, -w * 0.1, 0, -Math.max(150, d / 2 + 110));
    } else {
      /* walls: the part's back sits on the wall */
      const wallZ = (plan.size.d / 2) - plan.spec.relief - plan.plate.y1;
      if (look === 'paint') {
        put(new THREE.PlaneGeometry(4000, 3000), M.paint, 0, h / 2, wallZ).castShadow = false;
        /* an A4 frame beside it, centred on the same line: 230 × 320, 20 deep, a mount and a print */
        const fx = w / 2 + 70 + 115, fy = h / 2, fz = wallZ + 10;
        const ring = new THREE.Shape(); ring.moveTo(-115, -160); ring.lineTo(115, -160); ring.lineTo(115, 160); ring.lineTo(-115, 160); ring.closePath();
        const hole = new THREE.Path(); hole.moveTo(-99, -144); hole.lineTo(-99, 144); hole.lineTo(99, 144); hole.lineTo(99, -144); hole.closePath(); ring.holes.push(hole);
        const fr = put(new THREE.ExtrudeGeometry(ring, { depth: 20, bevelEnabled: false }), M.frame, fx, fy, wallZ);
        fr.castShadow = true;
        put(new THREE.PlaneGeometry(198, 288), M.mount, fx, fy, fz - 2).castShadow = false;
        put(new THREE.PlaneGeometry(148, 210), M.art, fx, fy + 6, fz - 1.8).castShadow = false;
        /* a light switch to the other side: 86 × 86 plate, a rocker */
        const sx = -w / 2 - 125, sy = Math.max(43, h / 2 - 70);
        put(new THREE.BoxGeometry(86, 86, 9), M.plate, sx, sy, wallZ + 4.5);
        put(new THREE.BoxGeometry(34, 52, 4), M.plate, sx, sy, wallZ + 10.5);
      } else if (look === 'shelf') {
        /* an olive wall, a 70 cm oak shelf 9 cm under the sign: books, a plant, a candle */
        put(new THREE.PlaneGeometry(4000, 3000), N.olivePaint, 0, h / 2, wallZ).castShadow = false;
        const sy = -110, sd = 190;
        box(700, 24, sd, N.oak, 0, sy - 12, wallZ + sd / 2);
        const books = [[26, 210, 150], [32, 190, 140], [22, 225, 160], [30, 200, 145]];
        books.reduce((x, [bw, bh, bd], i) => { box(bw, bh, bd, N.books[(i + 1) % 5], x + bw / 2, sy + bh / 2, wallZ + 12 + bd / 2); return x + bw + 1; }, Math.max(190, w / 2 + 40));
        roundPlant(-Math.max(250, w / 2 + 110), sy, wallZ + 90, 1);
        const cz = wallZ + 100;
        put(new THREE.CylinderGeometry(34, 34, 62, 32), N.potWhite, Math.max(190, w / 2 + 40) + 150, sy + 31, cz);
      } else {
        /* a bedroom: a blush wall, an upholstered headboard 14 cm under the sign, pillows against it */
        put(new THREE.PlaneGeometry(4000, 3000), N.blushPaint, 0, h / 2, wallZ).castShadow = false;
        const top = -140;
        rbox(1600, 620, 70, 30, N.fabric, 0, top - 310, wallZ + 35);
        rbox(620, 380, 150, 60, N.linen, -330, top - 150, wallZ + 150, 0).rotation.x = -0.16;
        rbox(620, 380, 150, 60, N.linenGrey, 330, top - 160, wallZ + 150, 0).rotation.x = -0.16;
        /* a wall lamp each side: a small plate, a drum shade glowing warm */
        for (const sx of [-1, 1]) {
          const lx = sx * Math.max(330, w / 2 + 170);
          rbox(44, 90, 10, 4, N.oak, lx, top + 120, wallZ + 5);
          put(new THREE.CylinderGeometry(48, 56, 105, 40, 1, true), N.shade, lx, top + 125, wallZ + 70);
        }
      }
    }
  }
  function setScene(name, variant = roomVariant) {
    sceneName = name; roomVariant = variant;
    const inRoom = name !== 'studio';
    room.visible = inRoom;
    /* in a room the room takes the shadows; the key light's shadow reaches the props */
    Object.assign(key.shadow.camera, inRoom ? { left: -420, right: 420, top: 420, bottom: -420 } : { left: -200, right: 200, top: 200, bottom: -200 });
    key.shadow.camera.updateProjectionMatrix();
    buildRoom();
    if (plan) { frame(plan); returning = true; }
    applyShadowPlanes();
    wake();
  }
  function applyShadowPlanes() {
    const sign = plan?.kind === 'sign', inRoom = sceneName !== 'studio';
    ground.visible = blob.visible = !sign && !inRoom;
    wall.visible = sign && !inRoom;
  }

  const controls = new OrbitControls(camera, renderer.domElement);
  Object.assign(controls, { enableDamping: true, dampingFactor: 0.08, enablePan: false, rotateSpeed: 0.7, zoomSpeed: 0.8, maxPolarAngle: Math.PI * 0.49, minPolarAngle: 0.15 });

  /* print-looking plastic: faint 0.2 mm layer lines that only appear when you get close */
  const layerLines = (m) => {
    m.onBeforeCompile = (sh) => {
      sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying float vNhZ;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvNhZ = (modelMatrix * vec4(transformed, 1.0)).y;');
      sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying float vNhZ;')
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        {
          float ph = vNhZ * 31.4159265;
          float fade = clamp(1.6 - fwidth(ph) * 0.45, 0.0, 1.0);
          vec3 upV = normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);
          float side = 1.0 - abs(dot(normal, upV));
          normal = normalize(normal + upV * sin(ph) * 0.2 * fade * side);
        }`);
    };
    m.customProgramCacheKey = () => 'nh-layers';
    return m;
  };
  const matCache = new Map();
  /* two colours on the holder: one body, cut at the filament change — the base colour below, the name's above */
  const clipUp = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), clipDown = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
  function plastic(c, clip = '') {
    const k = `${c.hex}|${c.finish}|${clip}`;
    if (matCache.has(k)) return matCache.get(k);
    const f = c.finish;
    const m = layerLines(new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(c.hex),
      roughness: f === 'matte' ? 0.8 : f === 'silk' ? 0.3 : 0.44,
      metalness: f === 'silk' ? 0.22 : 0,   // silk PLA is glossy plastic with a sheen, not metal
      clearcoat: f === 'silk' ? 0.6 : f === 'basic' ? 0.14 : 0,
      clearcoatRoughness: 0.45,
      sheen: f === 'matte' ? 0.3 : 0, sheenRoughness: 0.85, sheenColor: new THREE.Color(0xffffff),
      ...(clip ? { clippingPlanes: [clip === 'up' ? clipUp : clipDown], clipShadows: true } : {}),
    }));
    matCache.set(k, m);
    return m;
  }
  const penMats = new Map();
  const penMat = (hex, rough = 0.35, metal = 0) => {
    const k = `${hex}${rough}${metal}`;
    if (!penMats.has(k)) penMats.set(k, new THREE.MeshPhysicalMaterial({ color: new THREE.Color(hex), roughness: rough, metalness: metal, clearcoat: 0.3 }));
    return penMats.get(k);
  };

  /* pens, pencils and highlighters standing in the cups — the holder in use */
  function makePens(plan) {
    const g = new THREE.Group();
    const rnd = (i, k) => { const x = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return x - Math.floor(x); };
    const inks = ['#2F5DD8', '#D2312E', '#1B1B1D', '#2E9E6A', '#7A4FD0'];
    const lights = ['#F7E03C', '#FF7EB6', '#8FF06A', '#FF9F3A', '#6FD3FF'];
    plan.pockets.forEach((c, i) => {
      const item = new THREE.Group();
      const kind = c.kind === 'L' ? (rnd(i, 1) > 0.4 ? 'marker' : 'sharpie') : (rnd(i, 2) > 0.5 ? 'pen' : 'pencil');
      /* cylinders grow along Y; +90° about X stands them on the print frame's Z */
      const add = (geo, mat, z) => { const m = new THREE.Mesh(geo, mat); m.rotation.x = Math.PI / 2; m.position.z = z; m.castShadow = true; item.add(m); return m; };
      if (kind === 'pen') {
        const L = 128 + rnd(i, 3) * 16, col = inks[i % inks.length];
        add(new THREE.CylinderGeometry(4.3, 3.6, L * 0.66, 24), penMat('#F3F1EC', 0.25), L * 0.33);
        add(new THREE.CylinderGeometry(4.6, 4.4, L * 0.34, 24), penMat(col, 0.3), L * 0.83);
        const top = new THREE.Mesh(new THREE.SphereGeometry(4.6, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2), penMat(col, 0.3));
        top.rotation.x = Math.PI / 2; top.position.z = L; top.castShadow = true; item.add(top);
        const clip = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.6, L * 0.26), penMat('#C9CCD1', 0.25, 0.9));
        clip.position.set(0, 5, L * 0.85); item.add(clip);
      } else if (kind === 'pencil') {
        const L = 150 + rnd(i, 4) * 18;
        add(new THREE.CylinderGeometry(3.9, 3.9, L, 6), penMat('#F2B632', 0.5), L / 2);
        add(new THREE.CylinderGeometry(4, 4, 6, 24), penMat('#C9CCD1', 0.3, 0.9), L + 3);
        add(new THREE.CylinderGeometry(3.8, 3.8, 6, 24), penMat('#F48FA8', 0.7), L + 9);
      } else if (kind === 'marker') {
        const L = 118, col = lights[i % lights.length];
        add(new THREE.CylinderGeometry(7.2, 7.2, L * 0.68, 6), penMat('#FAFAF7', 0.3), L * 0.34);
        add(new THREE.CylinderGeometry(7.4, 7.6, L * 0.34, 6), penMat(col, 0.35), L * 0.84);
      } else {
        /* a fat permanent marker: grey barrel, black cap and a band */
        const L = 132;
        add(new THREE.CylinderGeometry(6.2, 6.2, L * 0.7, 28), penMat('#D9DBDE', 0.35, 0.3), L * 0.35);
        add(new THREE.CylinderGeometry(6.6, 6.4, L * 0.3, 28), penMat('#1B1B1D', 0.4), L * 0.85);
        add(new THREE.CylinderGeometry(6.3, 6.3, 5, 28), penMat(inks[(i + 2) % inks.length], 0.4), L * 0.62);
      }
      item.position.set(c.x, c.y, plan.spec.floor);
      /* a little lean backwards, away from the letters, and never two alike */
      item.rotation.set(-(0.05 + rnd(i, 5) * 0.1), (rnd(i, 6) - 0.5) * 0.16, rnd(i, 7) * Math.PI);
      g.add(item);
    });
    return g;
  }

  let holder = null, pensOn = true, plan = null;
  let fit = { dist: 400, target: new THREE.Vector3(0, 30, 0) };
  const HERO = { az: -0.38, polar: 1.1 };
  const sph = new THREE.Spherical(), orbit = new THREE.Spherical();
  function aim(immediate) {
    const d = fit.dist;
    sph.set(d, HERO.polar, HERO.az);
    const pos = new THREE.Vector3().setFromSpherical(sph).add(fit.target);
    if (immediate) { camera.position.copy(pos); controls.target.copy(fit.target); }
    return pos;
  }
  /* fit the part (and its pens) in the frame, whatever the stage's shape */
  function frame(p) {
    /* a 3-letter name should look like a small object, not fill the stage */
    const w = Math.max(p.size.w + (sceneName === 'studio' ? 0 : 250), 120), aspect = camera.aspect || 1.4;   // a room shows what is around it
    const H = Math.max(pensOn && p.kind !== 'sign' ? Math.max(p.size.h, 150) : p.size.h, 70);
    const half = (27 * Math.PI) / 360;
    const need = Math.max((w * 1.3) / aspect, H * 1.42) / 2 / Math.tan(half);
    fit = { dist: need + p.size.d, target: new THREE.Vector3(0, H * 0.44, 0) };
    controls.minDistance = Math.max(80, fit.dist * 0.28);
    controls.maxDistance = fit.dist * 2.4;
  }

  /* one plan → one holder group; the old one goes, the new one pops in */
  let pop = 1, lastSign = false;
  function show(next, colors, { ghost = false } = {}) {
    plan = { ...next, ghost };
    const geos = GEN.buildGeometries(THREE, plan);
    if (!geos) return null;
    if (holder) { holder.traverse((o) => { if (o.isMesh && o.userData.own) o.geometry.dispose(); }); scene.remove(holder); }
    holder = new THREE.Group();
    const inner = new THREE.Group();
    inner.rotation.x = -Math.PI / 2;
    const addMesh = (geo, mat) => { const m = new THREE.Mesh(geo, mat); m.castShadow = true; m.receiveShadow = true; m.userData.own = true; inner.add(m); return m; };
    if (plan.split) {
      // Put the preview seam just above the exact flat plinth face so both
      // clipped copies cannot draw that face and flicker. Manufacturing stays exact.
      const previewSplit = plan.split + 0.001;
      clipUp.constant = -previewSplit; clipDown.constant = previewSplit;
      addMesh(geos.body, plastic(colors.a, 'up'));
      addMesh(geos.body, plastic(colors.b, 'down')).userData.own = false;   // the same geometry, disposed once
    } else addMesh(geos.body, plastic(colors.b));
    addMesh(geos.letters, plastic(colors.a));
    addMesh(geos.steps, plastic(colors.a));
    if (pensOn) { const pens = makePens(plan); pens.name = 'pens'; inner.add(pens); }
    const depthMid = (plan.size.d / 2) - plan.spec.relief;
    inner.position.set(-plan.size.w / 2, 0, depthMid);
    holder.add(inner);
    scene.add(holder);
    blob.scale.set(plan.size.w * 1.5, plan.size.d * 3.2, 1);
    /* a sign hangs: no desk under it, a wall behind it (its back sits on the wall) */
    const sign = plan.kind === 'sign';
    if (sign) wall.position.set(0, 0, depthMid - plan.plate.y1 - 0.05);
    applyShadowPlanes();
    if (sceneName !== 'studio') buildRoom();
    if (sign !== lastSign) { lastSign = sign; if (VIEWS[homeName]) home = viewOf(homeName); returning = true; }
    const tris = [geos.body, geos.letters, geos.steps].reduce((s, g) => s + g.attributes.position.count / 3, 0);
    const first = !camera.userData.framed;
    frame(plan);
    if (first) { aim(true); camera.userData.framed = true; }
    pop = reduced() ? 1 : 0;
    wake();
    return { tris };
  }
  function setPens(on) {
    pensOn = on;
    if (plan) { frame(plan); returning = true; }
    if (holder) {
      const inner = holder.children[0];
      const old = inner.getObjectByName('pens');
      if (old) inner.remove(old);
      if (on && plan) { const pens = makePens(plan); pens.name = 'pens'; inner.add(pens); }
    }
    wake();
  }

  /* resting: ease back to the chosen view, and from the angled one sway a little so it reads as 3D */
  const VIEWS = { hero: HERO, front: { az: 0, polar: 1.42 }, top: { az: 0, polar: 0.28 }, back: { az: Math.PI - 0.32, polar: 1.36 } };
  /* a sign is seen as it hangs: square on, a touch from the side */
  const SIGN_HERO = { az: -0.42, polar: 1.36 };
  const viewOf = (name) => (name === 'hero' && plan?.kind === 'sign' ? SIGN_HERO : VIEWS[name] || VIEWS.hero);
  let home = VIEWS.hero, homeName = 'hero', homeZoom = 1;
  let lastTouch = performance.now() - 10000, returning = true, running = false, t0 = performance.now(), dragging = false;
  controls.addEventListener('start', () => { lastTouch = performance.now(); returning = false; dragging = true; onInteract?.('rotate'); wake(); });
  controls.addEventListener('end', () => { dragging = false; lastTouch = performance.now(); });
  controls.addEventListener('change', () => wake());
  /* the wheel scrolls the page; ctrl/⌘ + wheel (and a trackpad pinch, which sends ctrl) zooms.
     Caught on the way down, before OrbitControls on the canvas sees it. */
  host.addEventListener('wheel', (e) => {
    if (e.ctrlKey || e.metaKey) { onInteract?.('zoom'); lastTouch = performance.now(); returning = false; return; }
    e.stopPropagation();
    onWheelPass?.();
  }, { capture: true, passive: true });
  let visible = true;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) wake(); }, { threshold: 0.05 }).observe(host);

  /* The studio follows the plastic, the way a product photographer would light it. The name only
     reads through light and shade (letters and face are one colour), so:
       pale plastic  → a deeper backdrop, less fill, a stronger shadow: the relief casts real shade
       dark plastic  → more light, more reflection, a brighter rim: the edges catch the light
     `look` sets the aim; the loop eases every value there, so a colour change is a soft shift. */
  /* [darkest, palest]. Exposure and fill stay near neutral for dark plastic so charcoal still looks
     charcoal on screen; its form comes from the rim and reflections, which only touch the edges */
  const LOOK = { exposure: [0.96, 0.86], env: [0.62, 0.4], hemi: [0.34, 0.26], key: [3.4, 3.4], rim: [2.4, 0.5], shade: [0.16, 0.34] };
  const lookNow = {}, lookAim = {};
  const setLook = (s, now) => { for (const [k, [dark, pale]] of Object.entries(LOOK)) { lookAim[k] = dark + (pale - dark) * s; if (now || !(k in lookNow)) lookNow[k] = lookAim[k]; } };
  const applyLook = () => {
    renderer.toneMappingExposure = lookNow.exposure; scene.environmentIntensity = lookNow.env;
    hemi.intensity = lookNow.hemi; key.intensity = lookNow.key; rim.intensity = lookNow.rim; ground.material.opacity = lookNow.shade; wall.material.opacity = lookNow.shade * 0.8;
  };
  let pale = 0.3;
  setLook(pale, true); applyLook();
  function easeLook() {
    let moving = false;
    for (const k of Object.keys(LOOK)) { const d = lookAim[k] - lookNow[k]; if (Math.abs(d) > 1e-3) { lookNow[k] += d * 0.12; moving = true; } else lookNow[k] = lookAim[k]; }
    applyLook();
    return moving;
  }

  function loop(now) {
    running = false;
    if (!visible || document.hidden) return;
    const idle = now - lastTouch;
    let moving = controls.update();
    if (idle > 6000 && !dragging) returning = true;
    if (returning) {
      const sway = reduced() || idle > 25000 || homeName !== 'hero' ? 0 : Math.sin((now - t0) / 2200) * 0.1;
      /* travel round the part, not through it: ease the angles and the distance, not the position */
      orbit.setFromVector3(camera.position.clone().sub(controls.target));
      let dT = home.az + sway - orbit.theta; dT = Math.atan2(Math.sin(dT), Math.cos(dT));
      const dP = home.polar - orbit.phi, dR = fit.dist * homeZoom - orbit.radius;
      orbit.theta += dT * 0.07; orbit.phi += dP * 0.07; orbit.radius += dR * 0.07;
      controls.target.lerp(fit.target, 0.08);
      camera.position.setFromSpherical(orbit).add(controls.target);
      moving = moving || Math.abs(dT) > 1e-3 || Math.abs(dP) > 1e-3 || Math.abs(dR) > 0.05 || sway !== 0;
    }
    if (pop < 1 && holder) {
      pop = Math.min(1, pop + 1 / 18);
      const k = pop - 1, ease = 1 + 2.70158 * k * k * k + 1.70158 * k * k;   // easeOutBack
      holder.scale.setScalar(0.94 + 0.06 * ease);
      moving = true;
    }
    if (easeLook()) moving = true;
    renderer.render(scene, camera);
    placeRuler();
    if (moving) wake();
  }
  function wake() { if (!running) { running = true; requestAnimationFrame(loop); } }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) wake(); });

  /* the three measures, drawn over the canvas like a dimension drawing: the width along the
     front, the thickness along the end nearer the camera, the height standing at that corner */
  const tapes = ['w', 'd', 'h'].map((k) => {
    const el = document.createElement('div');
    el.className = `nh-ruler nh-ruler--${k}`; el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<i></i>';
    const tag = document.createElement('span');
    tag.className = 'nh-ruler__tag'; tag.setAttribute('aria-hidden', 'true');
    tag.innerHTML = '<small></small><b></b>';
    host.after(el, tag);
    return { k, el, tag, name: $('small', tag), value: $('b', tag) };
  });
  let dimsOn = true;
  const pA = new THREE.Vector3(), pB = new THREE.Vector3();
  function placeRuler() {
    const base = dimsOn && plan && holder && !plan.ghost && !dragging && sceneName === 'studio';
    const r = host.getBoundingClientRect();
    const { w = 0, d = 0, h = 0 } = plan?.size || {};
    const ex = (camera.position.x < 0 ? -1 : 1) * (w / 2 + 9);
    const toScreen = (v) => [(v.x + 1) / 2 * r.width, (1 - v.y) / 2 * r.height];
    const [cx, cy] = toScreen(pA.set(0, h / 2, 0).project(camera));
    const boxes = [];
    for (const tp of tapes) {
      let show = base && !!tp.value.textContent;
      if (show) {
        if (tp.k === 'w') { pA.set(-w / 2, 0, d / 2 + 9); pB.set(w / 2, 0, d / 2 + 9); }
        else if (tp.k === 'd') { pA.set(ex, 0, d / 2); pB.set(ex, 0, -d / 2); }
        else { pA.set(ex, 0, d / 2); pB.set(ex, h, d / 2); }
        pA.project(camera); pB.project(camera);
        const ax = (pA.x + 1) / 2 * r.width, ay = (1 - pA.y) / 2 * r.height, bx = (pB.x + 1) / 2 * r.width, by = (1 - pB.y) / 2 * r.height;
        const len = Math.hypot(bx - ax, by - ay);
        /* seen end-on (the thickness from the front, the height from above) it says nothing: hide it */
        if (len < (tp.k === 'w' ? 40 : 22)) show = false;
        else {
          tp.el.style.cssText = `--x:${ax}px;--y:${ay}px;--len:${len}px;--ang:${Math.atan2(by - ay, bx - ax)}rad`;
          /* the label sits beside its tape, on the side away from the part, and never on another label */
          let nx = -(by - ay) / len, ny = (bx - ax) / len;
          const mx = (ax + bx) / 2, my = (ay + by) / 2;
          if (nx * (mx - cx) + ny * (my - cy) < 0) { nx = -nx; ny = -ny; }
          const tw = tp.tag.offsetWidth || 90, th = tp.tag.offsetHeight || 22;
          let off = 8 + Math.abs(nx) * tw / 2 + Math.abs(ny) * th / 2, lx, ly;
          for (let tries = 0; tries < 6; tries++) {
            lx = clamp(mx + nx * off, tw / 2 + 8, r.width - tw / 2 - 8); ly = clamp(my + ny * off, th / 2 + 104, r.height - th / 2 - 100);   // clear of the tools above, the hint and the views below
            const hit = boxes.some((b) => Math.abs(b.x - lx) < (b.w + tw) / 2 + 6 && Math.abs(b.y - ly) < (b.h + th) / 2 + 6);
            if (!hit) break;
            off += th + 4;
          }
          boxes.push({ x: lx, y: ly, w: tw, h: th });
          tp.tag.style.cssText = `--x:${lx}px;--y:${ly}px`;
        }
      }
      tp.el.classList.toggle('is-on', show);
      tp.tag.classList.toggle('is-on', show);
    }
  }

  const resize = () => {
    const r = host.getBoundingClientRect();
    if (!r.width || !r.height) return;
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / r.height;
    camera.updateProjectionMatrix();
    if (plan) frame(plan);
    wake();
  };
  new ResizeObserver(resize).observe(host);
  resize();

  let gpu = '';
  try { const gl = renderer.getContext(); const ext = gl.getExtension('WEBGL_debug_renderer_info'); gpu = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)).slice(0, 60) : ''; } catch { /* ignore */ }

  return {
    THREE, gpu, show, setPens, debug: () => ({ camera, controls, fit, scene, holder, returning, lastTouch }),
    /* a square studio shot for the product photos: bone backdrop, print-bed lines, soft floor */
    snapshot({ size = 1600, az = HERO.az, polar = HERO.polar, zoom = 1, lift = 0 } = {}) {
      const keep = { w: renderer.domElement.width, h: renderer.domElement.height, pr: renderer.getPixelRatio(), aspect: camera.aspect };
      const c = document.createElement('canvas'); c.width = c.height = 1024;
      const g = c.getContext('2d');
      const grd = g.createRadialGradient(512, 380, 40, 512, 460, 760);
      const stops = pale > 0.5 ? ['#F1EEE8', '#E2DDD3', '#D3CDC1', '#C2BBAD'] : ['#FFFFFF', '#FBFAF6', '#F2F0E9', '#E6E1D5'];
      stops.forEach((c0, i) => grd.addColorStop([0, 0.45, 0.8, 1][i], c0));
      g.fillStyle = grd; g.fillRect(0, 0, 1024, 1024);
      for (let y = 640; y < 1024; y += 9) { g.fillStyle = `rgba(15,16,19,${0.05 * ((y - 640) / 384)})`; g.fillRect(0, y, 1024, 1); }
      const bg = new THREE.CanvasTexture(c); bg.colorSpace = THREE.SRGBColorSpace;
      scene.background = bg;
      const keepScene = sceneName;   // the cart's picture is always the studio shot
      if (keepScene !== 'studio') { sceneName = 'studio'; room.visible = false; applyShadowPlanes(); }
      renderer.setPixelRatio(1); renderer.setSize(size, size, false);
      camera.aspect = 1; camera.updateProjectionMatrix();
      if (plan) frame(plan);
      if (holder) holder.scale.setScalar(1);
      sph.set(fit.dist * zoom, polar, az);
      const target = fit.target.clone(); target.y += lift;
      camera.position.setFromSpherical(sph).add(target);
      controls.target.copy(target); camera.lookAt(target);
      renderer.render(scene, camera);
      const url = renderer.domElement.toDataURL('image/png');
      scene.background = null; bg.dispose();
      if (keepScene !== 'studio') { sceneName = keepScene; room.visible = true; applyShadowPlanes(); }
      renderer.setPixelRatio(keep.pr); renderer.setSize(keep.w / keep.pr, keep.h / keep.pr, false);
      camera.aspect = keep.aspect; camera.updateProjectionMatrix();
      if (plan) frame(plan);
      returning = true; wake();
      return url;
    },
    /* render one frame from a given angle now — for the harness, where the page may not animate */
    pose(az = HERO.az, polar = HERO.polar, zoom = 1) {
      lastTouch = performance.now(); returning = false;
      if (holder) holder.scale.setScalar(1);
      pop = 1;
      sph.set(fit.dist * zoom, polar, az);
      camera.position.setFromSpherical(sph).add(fit.target);
      controls.target.copy(fit.target); controls.update();
      renderer.render(scene, camera);
    },
    front() { this.view('hero'); },
    setScene, scene: () => sceneName, rooms: ROOMS,
    /* how pale the part is, 0 (black) … 1 (white), from its two colours by the area each covers */
    look(colors) {
      const lum = (hex) => { const c = new THREE.Color(hex); return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b; };   // linear light
      const L = lum(colors.b.hex) * 0.65 + lum(colors.a.hex) * 0.35;
      pale = Math.min(1, Math.max(0, (L - 0.04) / 0.6));
      setLook(pale); root.style.setProperty('--nh-deep', String(Math.max(0, Math.min(1, (L - 0.18) / 0.35)).toFixed(3)));
      wake();
      return pale;
    },
    /* hero (angled), front or top: the camera eases there and rests there */
    view(name) { homeName = VIEWS[name] ? name : 'hero'; home = viewOf(homeName); homeZoom = 1; lastTouch = performance.now() - 10000; returning = true; dragging = false; wake(); },
    /* { w: [name, value], d: […], h: […] }, or nothing to clear */
    ruler(dims) { for (const tp of tapes) { tp.name.textContent = dims?.[tp.k]?.[0] || ''; tp.value.textContent = dims?.[tp.k]?.[1] || ''; } wake(); },
    showDims(on) { dimsOn = on; wake(); },
    label(text) { renderer.domElement.setAttribute('aria-label', text); },
  };
}

/* ==========================================================================
   The studio
   ========================================================================== */
async function boot(root) {
  let cfg;
  try { cfg = JSON.parse($('[data-nh-config]', root).textContent); } catch { return; }
  const T = { ...EN, ...(cfg.t || {}) };
  const t = (k, vars = {}) => String(T[k] ?? k).replace(/\{(\w+)\}/g, (_, v) => (vars[v] ?? ''));
  const ar = String(cfg.locale || document.documentElement.lang || '').startsWith('ar');
  const colors = parseColors(cfg.colors);
  if (!colors.length) colors.push({ id: 'white', en: 'White', ar: 'أبيض', hex: '#F4F2EC', finish: 'basic' }, { id: 'black', en: 'Black', ar: 'أسود', hex: '#1B1B1D', finish: 'basic' });
  /* the sign's letters are their own colour; the holder's second colour is its base band, the rest is the name */
  const slotA = () => (params.kind === 'sign' || params.tone === 'kit' ? 'slot_letters' : 'slot_name');
  const slotB = () => (params.tone === 'kit' ? 'slot_body' : 'slot_base');
  const colorOf = (id) => colors.find((c) => c.id === id) || colors[0];
  const cname = (c) => (ar ? c.ar : c.en);
  const sname = (s) => (ar ? s.ar : s.en);
  const product = cfg.product || { variants: [], options: [] };
  const picker = variantPicker(product);
  const maxChars = Number(cfg.maxChars) || 12;
  const tiers = { small: Number(cfg.tiers?.small) || 4, medium: Number(cfg.tiers?.medium) || 8 };
  const genOpts = { maxChars, colors, tiers };

  const log = createLog({ endpoint: cfg.endpoint || '', version: `${GEN.GEN_VERSION}/${cfg.version || ''}`, debug: !!cfg.debug });
  /* first experiment: does seeing pens in the cups sell the idea better? */
  const pensArm = cfg.pens === false ? 'off' : log.exp('pens_default', ['on', 'off']);

  /* where the design starts: a shared link, else this visitor's draft, else the defaults */
  const q = new URLSearchParams(location.search);
  const mk = q.get('maker');
  const makerAuthorized = !!(mk && cfg.makerHash && (await sha256(mk)) === cfg.makerHash);
  const fromLink = GEN.decodeDesign(q.get('d'));
  let fromDraft = null;
  try { fromDraft = GEN.decodeDesign(localStorage.getItem('nh_draft')); } catch { /* ignore */ }
  const defaults = { ...GEN.DEFAULT_PARAMS, ...(cfg.defaults || {}) };
  let params = GEN.studioParams({ ...defaults, ...(fromLink || (fromDraft && { ...fromDraft, joiner: 'none' }) || {}) }, genOpts, {legacy:makerAuthorized});
  let rawText = (fromLink || fromDraft)?.text || '';
  /* how the design arrived: a shared link (nh=share), the homepage teaser (nh=home), any other link */
  const origin = fromLink ? (/^[a-z]{2,12}$/.test(q.get('nh') || '') ? q.get('nh') : 'link') : fromDraft ? 'draft' : 'fresh';
  let syncDock = () => {};   // set once the dock is wired, below
  let slot = 'a', pens = pensArm === 'on', plan = null, stage = null, stageState = 'loading', lastTier = null, caseTouched = (() => { const d = fromLink || fromDraft; return !!(d && d.case && d.case !== GEN.styleOf(d.style).case); })();

  /* ---------- render the controls ---------- */
  const controls = $('[data-nh-controls]', root);
  const styleCards = GEN.STYLES.map((s) => `
    <label class="nh-style" data-style="${s.id}">
      <input type="radio" name="nh-style-${cfg.section}" value="${s.id}" class="visually-hidden">
      <span class="nh-style__sample" data-sample aria-hidden="true"></span>
      <span class="nh-style__name">${esc(sname(s))}</span>
    </label>`).join('');
  const seg = (name, items, cls = '') => `<div class="nh-seg ${cls}" role="radiogroup">${items.map(([v, label, icon]) => `
    <label class="nh-seg__opt"><input type="radio" name="${name}-${cfg.section}" value="${v}" class="visually-hidden">
    <span>${icon ? svg(icon, 18) : ''}<b>${esc(label)}</b></span></label>`).join('')}</div>`;
  const swatches = colors.map((c) => `
    <label class="nh-swatch nh-swatch--${c.finish}" title="${esc(cname(c))} · ${esc(t(`finish_${c.finish}`))}" style="--c:${c.hex}">
      <input type="radio" name="nh-color-${cfg.section}" value="${c.id}" class="visually-hidden" aria-label="${esc(cname(c))}, ${esc(t(`finish_${c.finish}`))}">
      <span class="nh-swatch__dot"></span>
    </label>`).join('');
  /* symbols are drawn from the same outlines the printer gets — no emoji font decides the look */
  const symName = (id) => (SYMBOL_NAMES[id] || [id, id])[ar ? 1 : 0];
  const symIcon = (id, h = 20) => {
    const s = GEN.symbolSVG(id);
    if (!s) return '';
    const k = h / Math.max(1, s.width);
    return `<svg viewBox="0 0 ${s.width} 1" width="${Math.round(s.width * k)}" height="${Math.round(k)}" fill="currentColor" aria-hidden="true">${s.paths.map((d) => `<path fill-rule="evenodd" d="${d}"/>`).join('')}</svg>`;
  };
  /* two colours only when the product has a variant for it and the section offers it */
  const twoOk = picker.hasTone && cfg.twoTone !== false;
  if (!twoOk) params.tone = 'one';
  /* a pair whose colour is out of stock (removed from the list) simply isn't offered */
  const stocked = (id) => colors.find((c) => c.id === String(id || '').trim());
  const duos = twoOk ? (cfg.duos || []).map((d) => ({ a: stocked(d.a), b: stocked(d.b), label: d.label })).filter((d) => d.a && d.b) : [];
  /* steps after the name fold: one open at a time, each closed one shows what is chosen */
  const step = (n, key, title, body, open = false) => `
    <div class="nh-card nh-step-card${open ? ' is-open' : ''}" data-step="${key}">
      <button type="button" class="nh-card__head nh-card__toggle" aria-expanded="${open}" aria-controls="nh-${key}-${cfg.section}">
        <span class="nh-step">${n}</span><span class="nh-card__title">${esc(title)}</span>
        <span class="nh-card__value" data-val="${key}"></span><span class="nh-card__chev" aria-hidden="true">${svg('<path d="m6 9 6 6 6-6"/>', 18)}</span>
      </button>
      <div class="nh-card__body" id="nh-${key}-${cfg.section}"><div class="nh-card__inner">${body}</div></div>
    </div>`;
  const FEW = 10;   // swatches shown before "all colours"
  /* what it is, first: a holder for the desk or a sign for the wall — each with a little drawing */
  const ART = {
    holder: '<svg viewBox="0 0 120 72" aria-hidden="true"><path d="M40 40V8M54 40V4M70 40V12M84 40V6" stroke="currentColor" stroke-width="4" stroke-linecap="round" opacity=".55"/><path d="M12 66h96a3 3 0 0 0 3-3V38c-5-6-10 1-15-2s-9-7-14-2-10 2-15-1-10 5-15 1-9-5-14 0-9 2-12 4v25a3 3 0 0 0 3 3z" fill="currentColor" fill-opacity=".16" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round"/><path d="M26 54h68" stroke="currentColor" stroke-width="5" stroke-linecap="round"/></svg>',
    sign: '<svg viewBox="0 0 120 72" aria-hidden="true"><path d="M6 6h108" stroke="currentColor" stroke-width="1.6" stroke-dasharray="3 4" opacity=".35"/><circle cx="60" cy="14" r="2.6" fill="currentColor" opacity=".6"/><path d="M18 26c6-6 14-6 20-2 6-6 16-6 22 0 6-6 16-6 22 0 6-4 14-4 20 2 6 6 6 20 0 26-6 6-14 6-20 2-6 6-16 6-22 0-6 6-16 6-22 0-6 4-14 4-20-2-6-6-6-20 0-26z" fill="currentColor" fill-opacity=".16" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round"/><path d="M30 39h60" stroke="currentColor" stroke-width="6" stroke-linecap="round"/></svg>',
  };
  /* three steps: the name (and what it becomes), its shape, its colours */
  controls.innerHTML = `
    <div class="nh-card nh-card--name">
      <div class="nh-card__head"><span class="nh-step">01</span><label class="nh-card__title" for="nh-name-${cfg.section}">${esc(t('step_name'))}</label>
        <span class="nh-count num" data-count aria-live="polite"></span></div>
      <div class="nh-kinds nh-kinds--row" role="radiogroup" aria-label="${esc(t('kind_label'))}" data-group="kind">${GEN.KINDS.map((k) => `
        <label class="nh-kind"><input type="radio" name="nh-kind-${cfg.section}" value="${k}" class="visually-hidden">
          <span class="nh-kind__art">${ART[k]}</span><span class="nh-kind__txt"><b>${esc(t(`kind_${k}`))}</b><small>${esc(t(`kind_${k}_sub`))}</small></span></label>`).join('')}</div>
      <div class="nh-name">
        <input id="nh-name-${cfg.section}" class="nh-name__input" data-name type="text" autocomplete="off" autocorrect="off" spellcheck="false" enterkeyhint="done" maxlength="60" dir="auto" aria-describedby="nh-msg-${cfg.section}">
        <button type="button" class="nh-name__clear" data-clear aria-label="${esc(t('clear'))}" hidden>×</button>
      </div>
      <p class="nh-msg" id="nh-msg-${cfg.section}" data-msg role="status"></p>
      <div class="nh-symbols" role="group" aria-label="${esc(t('symbols'))}">
        <span class="nh-symbols__label">${esc(t('symbols'))}</span>
        <div class="nh-symbols__row">${GEN.SYMBOL_PRIMARY.map((id) => `<button type="button" class="nh-sym" data-sym="${esc(GEN.SYMBOL_CHAR[id])}" aria-label="${esc(symName(id))}" title="${esc(symName(id))}">${symIcon(id)}</button>`).join('')}
          <button type="button" class="nh-sym nh-sym--more" data-sym-more aria-expanded="false" aria-controls="nh-symgrid-${cfg.section}" aria-label="${esc(t('sym_more'))}">${svg('<path d="M12 5v14M5 12h14"/>', 16)}<span>${esc(t('sym_more'))}</span></button>
        </div>
        <div class="nh-symgrid" id="nh-symgrid-${cfg.section}" data-symgrid hidden>${GEN.SYMBOL_IDS.map((id) => `
          <button type="button" class="nh-symgrid__item" data-sym="${esc(GEN.SYMBOL_CHAR[id])}" title="${esc(symName(id))}"><span aria-hidden="true">${symIcon(id, 22)}</span><small>${esc(symName(id))}</small></button>`).join('')}
        </div>
      </div>
    </div>
    ${step('02', 'shape', t('step_shape'), `
      <div class="nh-styles" data-styles role="radiogroup" aria-label="${esc(t('step_style'))}">${styleCards}</div>
      <div class="nh-sub" data-sub="layout">
        <span class="nh-sub__label">${esc(t('step_layout'))}</span>
        <div data-group="layout" role="radiogroup" aria-label="${esc(t('step_layout'))}">${seg('nh-layout', GEN.LAYOUTS.map((l) => [l, t(`layout_${l}`), ICON[l]]))}</div>
        <div class="nh-row">
          <span class="nh-row__label" id="nh-case-${cfg.section}">${esc(t('case_label'))}</span>
          <div aria-labelledby="nh-case-${cfg.section}" data-group="case">${seg('nh-case', [['upper', t('case_upper')], ['title', t('case_title')]], 'nh-seg--mini')}</div>
        </div>
        <div class="nh-row" data-lines-row hidden>
          <span class="nh-row__label" id="nh-lines-${cfg.section}">${esc(t('lines_label'))}</span>
          <div aria-labelledby="nh-lines-${cfg.section}" data-group="lines">${seg('nh-lines', [['1', t('lines_1')], ['2', t('lines_2')]], 'nh-seg--mini')}</div>
        </div>
      </div>
      <div class="nh-sub" data-sub="pockets">
        <span class="nh-sub__label">${esc(t('step_pockets'))}</span>
        <div data-group="pockets" role="radiogroup" aria-label="${esc(t('step_pockets'))}">${seg('nh-pockets', GEN.POCKET_MODES.map((m) => [m, t(`pockets_${m}`), ICON[m]]), 'nh-seg--cups')}</div>
      </div>
      <div class="nh-sub" data-sub="hang">
        <span class="nh-sub__label">${esc(t('step_hang'))}</span>
        <div data-group="hang" role="radiogroup" aria-label="${esc(t('step_hang'))}">${seg('nh-hang', GEN.HANGS.map((m) => [m, t(`hang_${m}`), ICON[`hang_${m}`]]))}</div>
        <p class="nh-hint nh-hangnote" data-hang-note aria-live="polite"></p>
      </div>`, true)}
    ${step('03', 'color', t('step_color'), `
      <div data-group="tone" ${twoOk ? '' : 'hidden'}>${seg('nh-tone', [['one', t('tone_one')], ['two', t('tone_two')], ['kit', t('tone_kit')]])}<p class="nh-tonenote" data-tone-note hidden></p></div>
      ${duos.length ? `<div class="nh-duos" data-duos-wrap><span class="nh-row__label">${esc(t('duos'))}</span><div class="nh-duos__row">${duos.map((d, i) => `
        <button type="button" class="nh-duo" data-duo="${i}" title="${esc(d.label || `${cname(d.a)} + ${cname(d.b)}`)}" aria-label="${esc(d.label || `${cname(d.a)} + ${cname(d.b)}`)}">
          <span style="--a:${d.a.hex};--b:${d.b.hex}"></span></button>`).join('')}</div></div>` : ''}
      <div class="nh-slots" data-slots hidden role="tablist">
        <button type="button" role="tab" class="nh-slot" data-slot="a"><span class="nh-slot__dot" data-slot-dot="a"></span><span data-slot-a>${esc(t('slot_letters'))}</span></button>
        <button type="button" role="tab" class="nh-slot" data-slot="b"><span class="nh-slot__dot" data-slot-dot="b"></span><span data-slot-b>${esc(t('slot_base'))}</span></button>
      </div>
      <div class="nh-swatches" role="radiogroup" aria-label="${esc(t('step_color'))}" data-group="color">${swatches}</div>
      <div class="nh-colorfoot">
        <p class="nh-colornow" data-color-now aria-live="polite"></p>
        ${colors.length > FEW + 2 ? `<button type="button" class="nh-linkbtn" data-all-colors aria-expanded="false"></button>` : ''}
      </div>`, true)}

    <div class="nh-card nh-card--buy" data-buy>
      <div class="nh-tiers" aria-hidden="true">${[0, 1, 2].map((i) => `<span data-tier="${i}"><b>${esc(t(`size_${i}`))}</b><small class="num">${esc(t('tier_range', { a: i === 0 ? 1 : i === 1 ? tiers.small + 1 : tiers.medium + 1, b: i === 0 ? tiers.small : i === 1 ? tiers.medium : maxChars }))}</small></span>`).join('')}</div>
      <div class="nh-total">
        <div><p class="nh-price num" data-price aria-live="polite"></p><p class="nh-spec num" data-spec></p></div>
        <button type="button" class="nh-iconbtn" data-share aria-label="${esc(t('share'))}" title="${esc(t('share'))}">${svg(ICON.share)}</button>
      </div>
      <div class="nh-actions">
        <button type="button" class="btn btn--lime btn--block nh-add" data-add>${esc(t('add'))}</button>
        <button type="button" class="btn btn--ghost btn--block nh-buy" data-buynow>${esc(t('buy'))}</button>
      </div>
      <p class="nh-note">${svg(ICON.check, 16)}<span data-make-note>${esc(t('no_supports'))}</span></p>
      ${cfg.lead ? `<p class="nh-note">${svg('<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>', 16)}<span>${esc(cfg.lead)}</span></p>` : ''}
    </div>`;

  /* price and add, always within reach: pinned to the foot of the screen until the full buy card shows */
  controls.insertAdjacentHTML('afterend', `
    <div class="nh-dock" data-dock data-bottom-bar data-bottom-bar-when="is-on">
      <div class="nh-dock__sum"><span class="nh-dock__price num" data-dock-price></span><span class="nh-dock__spec num" data-dock-spec></span></div>
      <button type="button" class="btn btn--lime nh-dock__add" data-dock-add>${esc(t('add'))}</button>
    </div>`);
  const dock = $('[data-dock]', root), dockPrice = $('[data-dock-price]', dock), dockSpec = $('[data-dock-spec]', dock), dockAdd = $('[data-dock-add]', dock);

  /* the accordion: a closed step opens (and the open one closes); the open one folds on a second tap */
  const openStep = (key, { scroll = false } = {}) => {
    $$('[data-step]', controls).forEach((c) => {
      const on = c.dataset.step === key && !c.classList.contains('is-open');
      c.classList.toggle('is-open', on);
      $('.nh-card__toggle', c).setAttribute('aria-expanded', String(on));
      if (on && scroll) {
        setTimeout(() => {
          const r = c.getBoundingClientRect();
          if (r.top < 80 || r.top > innerHeight * 0.55) c.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
        }, 280);
      }
    });
  };
  controls.addEventListener('click', (e) => {
    const h = e.target.closest('.nh-card__toggle');
    if (!h) return;
    const card = h.closest('[data-step]');
    openStep(card.dataset.step, { scroll: true });
    log.count(`step_${card.dataset.step}`);
  });
  /* colours: the first few, whichever is chosen, and the rest on request */
  const allColorsBtn = $('[data-all-colors]', controls);
  function syncSwatches() {
    const all = allColorsBtn?.getAttribute('aria-expanded') === 'true';
    const cur = [params.color, params.tone !== 'one' ? params.color2 : ''];
    $$('[data-group="color"] .nh-swatch', controls).forEach((l, i) => { l.hidden = !!allColorsBtn && !all && i >= FEW && !cur.includes($('input', l).value); });
    if (allColorsBtn) allColorsBtn.textContent = all ? t('colors_less') : t('colors_all', { n: colors.length });
  }
  allColorsBtn?.addEventListener('click', () => {
    allColorsBtn.setAttribute('aria-expanded', String(allColorsBtn.getAttribute('aria-expanded') !== 'true'));
    syncSwatches(); log.count('colors_all');
  });

  const input = $('[data-name]', controls);
  const msg = $('[data-msg]', controls);
  const addBtn = $('[data-add]', controls), buyBtn = $('[data-buynow]', controls);
  input.value = rawText;

  /* ---------- the stage chrome ---------- */
  const stageEl = $('[data-nh-stage]', root);
  const flat = $('[data-nh-flat]', stageEl);
  const tools = $('[data-nh-tools]', stageEl);
  tools.innerHTML = `
    <button type="button" class="nh-tool" data-surprise title="${esc(t('surprise'))}">${svg(ICON.dice, 18)}<span>${esc(t('surprise'))}</span></button>
    <button type="button" class="nh-tool" data-pens aria-pressed="${pens}" title="${esc(t('pens'))}">${svg(ICON.pen, 18)}<span>${esc(t('pens'))}</span></button>
    <button type="button" class="nh-tool" data-dims aria-pressed="true" title="${esc(t('dims'))}">${svg(ICON.ruler, 18)}<span>${esc(t('dims'))}</span></button>`;
  tools.setAttribute('aria-label', t('view_label'));
  /* the view presets, bottom corner of the stage */
  stageEl.insertAdjacentHTML('beforeend', `<div class="nh__views" role="radiogroup" aria-label="${esc(t('view_label'))}" data-views>${['hero', 'front', 'top', 'back'].map((v) => `
    <button type="button" role="radio" aria-checked="${v === 'hero'}" data-view="${v}">${esc(t(`view_${v}`))}</button>`).join('')}</div>
    <p class="nh__example" data-example aria-hidden="true">${esc(t('example'))}</p>`);
  /* where to see it: the studio, or in a room at life size — a desk for the holder, a wall for the sign */
  stageEl.insertAdjacentHTML('beforeend', `<div class="nh__scenes" role="radiogroup" aria-label="${esc(t('scene_label'))}" data-scenes>${['studio', 'desk', 'wall'].map((v) => `
    <button type="button" role="radio" aria-checked="${v === 'studio'}" data-scene="${v}">${esc(t(`scene_${v}`))}</button>`).join('')}</div>
    <div class="nh__looks" role="radiogroup" aria-label="${esc(t('look_label'))}" data-looks hidden></div>
    <p class="nh__scale" data-scale aria-live="polite"></p>`);
  /* each room comes in three looks, all life-size; the scale line names what to judge the size by */
  const LOOKS = { desk: ['oak', 'setup', 'study'], wall: ['paint', 'shelf', 'bedroom'] };
  let sceneNow = 'studio', lookNow = 0;
  const setScene = (v, via = 'click', look = v === sceneNow ? lookNow : 0) => {
    sceneNow = v; lookNow = look;
    $$('[data-scene]', stageEl).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.scene === v)));
    stageEl.dataset.scene = v;
    const looks = $('[data-looks]', stageEl);
    looks.hidden = v === 'studio';
    looks.innerHTML = v === 'studio' ? '' : LOOKS[v].map((k, i) => `<button type="button" role="radio" aria-checked="${i === look}" data-look="${i}">${esc(t(`look_${k}`))}</button>`).join('');
    $('[data-scale]', stageEl).textContent = v === 'studio' ? '' : t(`scale_${LOOKS[v][look]}`);
    stage?.setScene(v, look);
    if (via === 'click') log.track('view', { action: v === 'studio' ? 'scene_studio' : `scene_${v}_${LOOKS[v][look]}` });
  };
  $('[data-scenes]', stageEl).addEventListener('click', (e) => { const b = e.target.closest('[data-scene]'); if (b) setScene(b.dataset.scene); });
  $('[data-looks]', stageEl).addEventListener('click', (e) => { const b = e.target.closest('[data-look]'); if (b) setScene(sceneNow, 'click', Number(b.dataset.look)); });
  const setView = (v) => { $$('[data-view]', stageEl).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.view === v))); stage?.view(v); };
  $('[data-views]', stageEl).addEventListener('click', (e) => { const b = e.target.closest('[data-view]'); if (b) { setView(b.dataset.view); log.track('view', { action: b.dataset.view }); } });
  const hint = $('[data-nh-hint]', stageEl);
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  if (hint) hint.textContent = t(coarse ? 'drag_hint' : 'drag_hint_desk');
  const example = $('[data-example]', stageEl);
  /* a plain wheel over the stage scrolls the page; say once how to zoom instead */
  let wheelSaid = false;
  const onWheelPass = () => {
    if (wheelSaid || !hint) return;
    wheelSaid = true;
    hint.textContent = t('zoom_hint'); hint.classList.remove('is-gone'); hint.classList.add('is-flash');
    setTimeout(() => { hint.classList.remove('is-flash'); hint.classList.add('is-gone'); }, 2600);
  };
  const dims = $('[data-nh-dims]', stageEl);

  /* ---------- state → UI ---------- */
  const setRadio = (group, value) => { $$(`[data-group="${group}"] input`, controls).forEach((i) => { i.checked = i.value === value; }); };
  function syncControls() {
    $$('[data-styles] input', controls).forEach((i) => { i.checked = i.value === params.style; });
    setRadio('layout', params.layout); setRadio('case', params.case === 'asis' ? 'title' : params.case);
    setRadio('tone', params.tone); setRadio('pockets', params.pockets); setRadio('kind', params.kind); setRadio('hang', params.hang);
    /* a sign has no room inside and no pens; it hangs, and its back is worth a look */
    const sign = params.kind === 'sign';
    $('[data-sub="pockets"]', controls).hidden = sign || params.construction==='letters';
    $('[data-sub="hang"]', controls).hidden = !sign;
    $('[data-pens]', tools).hidden = sign;
    $('[data-view="top"]', stageEl).hidden = sign;
    $('[data-view="back"]', stageEl).hidden = !sign;
    $('[data-slot-a]', controls).textContent = t(slotA());
    $('[data-slot-b]', controls).textContent = t(slotB());
    /* how it is made, as chosen: one upright piece, a flat sign, or a holder and its letters */
    $('[data-make-note]', controls).textContent = t(['letters','organizer'].includes(params.construction) ? 'solid_make' : sign ? 'no_supports_sign' : params.tone === 'kit' ? 'no_supports_kit' : 'no_supports');
    /* on the holder, two colours come two ways; the sign has one */
    $('[data-group="tone"] input[value="kit"]', controls).closest('label').hidden = sign || ['letters','organizer'].includes(params.construction);
    const note = $('[data-tone-note]', controls);
    note.hidden = sign || params.tone === 'one';
    note.textContent = sign || params.tone === 'one' ? '' : t(params.tone === 'kit' ? 'note_kit' : 'note_base');
    /* a desk for the holder, a wall for the sign; a room follows the kind across */
    $('[data-scene="desk"]', stageEl).hidden = sign;
    $('[data-scene="wall"]', stageEl).hidden = !sign;
    if (sceneNow === 'desk' && sign) setScene('wall', 'kind'); else if (sceneNow === 'wall' && !sign) setScene('desk', 'kind');
    const onView = $('[data-view][aria-checked="true"]', stageEl);
    if (onView?.hidden) setView('hero');
    stageEl.dataset.kind = params.kind;
    /* the heading says what is being made */
    const h1 = $('.nh__title', root);
    if (h1) { h1.dataset.holder ??= h1.textContent; h1.textContent = sign ? t('title_sign') : h1.dataset.holder; }
    const lead = $('.nh__lead', root);
    if (lead) { lead.dataset.holder ??= lead.textContent; lead.textContent = sign ? t('lead_sign') : lead.dataset.holder; }
    const two = params.tone !== 'one';
    $('[data-slots]', controls).hidden = !two;
    if (!two) slot = 'a';
    $$('[data-slot]', controls).forEach((b) => { b.setAttribute('aria-selected', String(b.dataset.slot === slot)); });
    $('[data-slot-dot="a"]', controls).style.background = colorOf(params.color).hex;
    $('[data-slot-dot="b"]', controls).style.background = colorOf(params.color2).hex;
    const cur = slot === 'b' ? params.color2 : params.color;
    setRadio('color', cur);
    syncSwatches();
    const val = (k, text) => { const el = $(`[data-val="${k}"]`, controls); if (el) el.textContent = text; };
    const st = GEN.styleOf(params.style);
    /* the shape step, closed, still says everything in it */
    val('shape', [sname(st), t(`layout_${params.layout}`), params.kind === 'sign' ? t(`hang_${plan?.hang?.kind || params.hang}`) : params.construction==='letters' ? 'Ø12 mm' : t(`pockets_${params.pockets}`)].join(' · '));
    val('color', colorName());
    const curC = colorOf(cur);
    $('[data-color-now]', controls).innerHTML = `<span class="nh-colornow__dot" style="--c:${curC.hex}"></span>${two ? `<b>${esc(t(slot === 'b' ? slotB() : slotA()))}:</b> ` : ''}${esc(cname(curC))} · ${esc(t(`finish_${curC.finish}`))}`;
    $('[data-clear]', controls).hidden = !input.value;
    $('[data-pens]', tools).setAttribute('aria-pressed', String(pens));
    const style = GEN.styleOf(params.style);
    $$('[data-group="layout"] input[value="bounce"]', controls).forEach((i) => { i.disabled = !!style.connected; i.closest('label').classList.toggle('is-disabled', !!style.connected); });
  }

  /* where to drill: the numbers that go on the order, and under the hanging choice */
  function hangText() {
    if (!plan || plan.kind !== 'sign' || !params.text) return '';
    const cmOf = (mm) => (mm / 10).toFixed(1), hg = plan.hang;
    if (hg.holes.length === 2) return t('hang_two', { s: cmOf(hg.spacing), t: cmOf(hg.fromTop) });
    if (hg.holes.length === 1) return t('hang_one', { t: cmOf(hg.fromTop) });
    return params.hang === 'tape' ? t('hang_tape_note') : t('hang_fallback');
  }
  function colorName() { return params.tone !== 'one' ? `${cname(colorOf(params.color))} + ${cname(colorOf(params.color2))}` : cname(colorOf(params.color)); }

  /* each style card shows the name in its own font */
  const samples = $$('[data-sample]', controls);
  function syncSamples() {
    const text = params.text || t('ghost');
    samples.forEach((el) => {
      const s = GEN.styleOf(el.closest('[data-style]').dataset.style);
      el.textContent = GEN.applyCase(text, s.case);
      el.style.fontFamily = `nh-${s.id}, var(--f-latin, system-ui)`;
      const len = Math.max(3, GEN.countChars(text));
      el.style.fontSize = `${Math.max(15, Math.min(34, 150 / len))}px`;
    });
  }
  function syncFlat() {
    if (!flat) return;
    const s = GEN.styleOf(params.style);
    flat.textContent = GEN.applyCase(params.text || t('ghost'), params.case || s.case);
    flat.style.fontFamily = `nh-${s.id}, var(--f-latin, system-ui)`;
    flat.style.setProperty('--a', colorOf(params.color).hex);
    flat.style.setProperty('--b', colorOf(params.tone !== 'one' ? params.color2 : params.color).hex);
    flat.classList.toggle('is-ghost', !params.text);
    /* the same deeper studio behind pale plastic as the 3D uses, before (or without) the 3D */
    if (!stage) {
      const lin = (h) => { const v = parseInt(h, 16) / 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
      const lum = (hex) => { const m = hex.slice(1).match(/../g); return 0.2126 * lin(m[0]) + 0.7152 * lin(m[1]) + 0.0722 * lin(m[2]); };
      const L = lum(colorOf(params.tone !== 'one' ? params.color2 : params.color).hex) * 0.65 + lum(colorOf(params.color).hex) * 0.35;
      stageEl.style.setProperty('--nh-deep', String(Math.max(0, Math.min(1, (L - 0.18) / 0.35)).toFixed(3)));
    }
    example?.classList.toggle('is-on', !params.text);
  }

  const variant = () => picker.pick(plan?.tier ?? GEN.tierOf(GEN.countChars(params.text), tiers), params.tone);
  function problem() {
    if (params.text && (!plan || GEN.encodeDesign(plan.params)!==GEN.encodeDesign(params))) return {code:'building',text:t('loading')};
    if (!params.text) return { code: 'empty', text: t('err_empty') };
    if (plan?.unsupported?.length) return { code: 'unsupported', text: t('err_unsupported', { chars: plan.unsupported.join(' ') }) };
    if (plan?.warnings?.includes('too_long')) return { code: 'too_long', text: t('err_too_long') };
    const v = variant();
    if (v && v.available === false) return { code: 'sold_out', text: t('err_sold_out') };
    return null;
  }
  function syncSummary() {
    const n = GEN.countChars(params.text);
    $('[data-count]', controls).textContent = t('chars', { n, max: maxChars });
    const v = variant();
    const tier = plan?.tier ?? GEN.tierOf(n, tiers);
    $$('[data-tier]', controls).forEach((el) => el.classList.toggle('is-on', Number(el.dataset.tier) === tier && n > 0));
    const price = v ? money(v.price, cfg.money) : '';
    /* on sale: the old price struck beside the new one, in the card and in the dock */
    const was = v && v.compare_at_price > v.price ? ` <s>${esc(money(v.compare_at_price, cfg.money))}</s>` : '';
    $('[data-price]', controls).innerHTML = esc(price) + was;
    dockPrice.innerHTML = esc(price) + was;
    const two = picker.pick(tier, 'two'), one = picker.pick(tier, 'one');
    const extra = two && one && two !== one ? two.price - one.price : 0;
    for (const [tv, key] of [['two', params.kind === 'sign' ? 'tone_two' : 'tone_base'], ['kit', 'tone_kit']]) {
      const b = $(`[data-group="tone"] input[value="${tv}"] + span b`, controls);
      if (b) b.innerHTML = extra > 0 ? `${esc(t(key))} <small>+${esc(money(extra, cfg.money))}</small>` : esc(t(key));
    }
    const spec = plan && !plan.empty
      ? t('summary', { w: (plan.size.w / 10).toFixed(1), d: (plan.size.d / 10).toFixed(1), h: (plan.size.h / 10).toFixed(1), g: `~${plan.grams}` })
      : '';
    $('[data-spec]', controls).textContent = spec;
    dockSpec.textContent = params.text ? spec : '';
    const hn = $('[data-hang-note]', controls);
    if (hn) hn.textContent = hangText();
    const cm = (mm) => `${(mm / 10).toFixed(1)} ${t('cm')}`;
    stage?.ruler(plan && !plan.empty && params.text ? { w: [t('dim_w'), cm(plan.size.w)], d: [t('dim_d'), cm(plan.size.d)], h: [t('dim_h'), cm(plan.size.h)] } : null);
    if (dims) { dims.textContent = plan && !plan.empty ? `${(plan.size.w / 10).toFixed(1)} × ${(plan.size.d / 10).toFixed(1)} × ${(plan.size.h / 10).toFixed(1)} ${t('cm')}` : ''; dims.hidden = !plan || plan.empty || !params.text; }
    const cupsLabels = $$('[data-group="pockets"] label', controls);
    /* each width says how much room it leaves inside */
    cupsLabels.forEach((l) => {
      const mode = $('input', l).value;
      let small = $('small', l);
      if (!small) { small = document.createElement('small'); small.className = 'num'; $('span', l).appendChild(small); }
      const spec=params.v===4?GEN.ORGANIZER_V4_SPEC:GEN.ORGANIZER_SPEC;
      small.textContent = t('inside', { n: ((params.construction==='organizer' ? spec.rearDepth[mode]-2*spec.wall : GEN.TROUGH[mode]) / 10).toFixed(1) });
    });
    const linesRow = $('[data-lines-row]', controls);
    linesRow.hidden = !(plan && !plan.empty && !plan.ghost && plan.canStack);
    if (!linesRow.hidden) setRadio('lines', String(plan.lines));
    const p = problem();
    msg.textContent = p && p.code !== 'empty' ? p.text : plan?.warnings?.includes('small_letters') ? t('warn_small', { cap: Math.round(plan.cap) }) : '';
    msg.classList.toggle('is-error', !!p && p.code !== 'empty');
    for (const b of [addBtn, buyBtn, dockAdd]) b.setAttribute('aria-disabled', String(!!p || !plan));
    if (!dockAdd.dataset.done) dockAdd.textContent = p?.code === 'empty' ? t('err_empty') : t('add');
    addBtn.dataset.label = p ? '' : `${t('add')} · ${price}`;
    if (!addBtn.classList.contains('is-busy') && !addBtn.dataset.done) addBtn.textContent = p?.code === 'empty' ? t('err_empty') : `${t('add')} · ${price}`;
    if (tier !== lastTier && n > 0) { log.track('price', { tier, price: v?.price, variant: v?.id }); lastTier = tier; }
    syncDock();
  }

  /* ---------- building ---------- */
  let buildId = 0, fontsFailed = false;
  const opentypeP = () => import('opentype').then((m) => m.default || m);
  async function rebuild() {
    const id = ++buildId;
    const style = GEN.styleOf(params.style);
    const timer = log.time('build');
    let fonts;
    try {
      const ot = await opentypeP();
      fonts = await GEN.loadStyleFonts(ot, style);
      fontsFailed = false;
    } catch (e) {
      fontsFailed = true;
      log.error(e, 'fonts');
      if (id === buildId) setStage('error', t('err_fonts'));
      return;
    }
    if (id !== buildId) return;
    const provider = GEN.fontProvider(fonts, {quality:['letters','organizer'].includes(params.construction)?64:24});
    const ghost = !params.text;
    const shown = ghost ? { ...params, text: t('ghost'), lines: '1' } : params;
    const next = GEN.planModel(shown, provider, genOpts);
    try {await GEN.prepareSolidPlan(next);} catch(e) {
      log.error(e,'solid');if(id===buildId) {plan=null;setStage('error',t('err_model'));syncSummary();}return;
    }
    if(id!==buildId)return;
    plan = ghost ? { ...next, tier: 0, unsupported: [], ghost: true } : next;
    syncSummary();
    let tris = 0;
    if (stage && !next.empty) {
      const colorsNow = { a: colorOf(params.color), b: colorOf(params.tone !== 'one' ? params.color2 : params.color) };
      tris = stage.show(next, colorsNow, { ghost })?.tris || 0;
      stage.look(colorsNow);
      stage.label(t('aria_preview', { text: params.text || '…', style: sname(style), color: colorName() }));
      if (stageState !== 'ready') setStage('ready');
    }
    const perf = timer({ tris });
    if (perf.ms > 250 || Math.random() < 0.15) log.track('perf', perf);
    /* a character no style could draw is a request for the next font — log each once */
    const missing = ghost ? '' : `${style.id}:${next.unsupported.join('')}`;
    if (next.unsupported.length && missing !== lastMissing) log.track('unsupported', { chars: next.unsupported, style: style.id });
    lastMissing = missing;
  }
  let lastMissing = '';
  let rebuildTimer = null;
  const schedule = (ms = 90) => { clearTimeout(rebuildTimer); rebuildTimer = setTimeout(() => { rebuildTimer = null; rebuild(); }, ms); };

  /* ---------- changes ---------- */
  let urlTimer = null;
  function persist() {
    const code = GEN.encodeDesign(params);
    try { localStorage.setItem('nh_draft', code); } catch { /* ignore */ }
    clearTimeout(urlTimer);
    urlTimer = setTimeout(() => {
      const u = new URL(location.href);
      if (params.text) u.searchParams.set('d', code); else u.searchParams.delete('d');
      u.searchParams.delete('nh');
      history.replaceState(history.state, '', u);
    }, 400);
  }
  function dimsNow() {
    return { text: params.text, len: GEN.countChars(params.text), style: params.style, layout: params.layout, joiner: params.joiner, case: params.case, pockets: params.pockets, tone: params.tone, color: params.color, color2: params.tone !== 'one' ? params.color2 : '', lines: plan?.lines || 1, kind: params.kind, hang: params.kind === 'sign' ? params.hang : '' };
  }
  function set(patch, via = 'click') {
    const prev = params;
    params = GEN.studioParams({ ...params, ...patch }, genOpts, {legacy:makerAuthorized});
    if (!twoOk) params.tone = 'one';
    if (GEN.styleOf(params.style).connected && params.layout === 'bounce') params.layout = 'wave';
    for (const k of ['style', 'layout', 'joiner', 'case', 'pockets', 'tone', 'lines', 'kind', 'hang']) {
      if (prev[k] !== params[k]) { log.track(k, { from: prev[k], to: params[k], via }); log.tried(k, params[k]); }
    }
    if (prev.color !== params.color) { log.track('color', { slot: 'a', id: params.color, via }); log.tried('color', params.color); }
    if (prev.color2 !== params.color2 && params.tone !== 'one') { log.track('color', { slot: 'b', id: params.color2, via }); log.tried('color', params.color2); }
    log.dims(dimsNow());
    syncControls(); syncFlat(); syncSamples(); syncSummary(); persist();
    if (prev.style !== params.style) warmStyle(params.style);
    schedule(prev.text !== params.text ? 110 : 10);
  }

  /* name typing: debounced log, grapheme cap, symbols from the chips */
  let nameTimer = null, keystrokes = 0;
  function onName(via = 'typed') {
    let v = input.value;
    const over = GEN.countChars(v) > maxChars;
    if (over) { v = GEN.cleanText(v, maxChars); input.value = v; log.count('limit_hit'); }
    rawText = v;
    keystrokes += 1; log.edit();
    set({ text: v }, via);
    if (over) { msg.textContent = t('err_limit', { max: maxChars }); msg.classList.add('is-error'); }
    clearTimeout(nameTimer);
    nameTimer = setTimeout(() => {
      const g = [...(params.text || '')];
      const typedSymbols = g.filter((c) => GEN.symbolFor(c));
      log.track('name', {
        text: params.text, len: GEN.countChars(params.text), words: params.text ? params.text.split(' ').length : 0,
        script: /[؀-ۿ]/.test(params.text) ? (/[A-Za-z]/.test(params.text) ? 'mixed' : 'arabic') : /[A-Za-z]/.test(params.text) ? 'latin' : 'other',
        symbols: typedSymbols.join(''), digits: /\d/.test(params.text) ? 1 : 0, keys: keystrokes, via,
      });
      keystrokes = 0;
    }, 900);
  }
  input.addEventListener('input', () => onName('typed'));
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); input.blur(); } });
  input.addEventListener('focus', () => { stopPlaceholder(); log.count('name_focus'); }, { once: false });
  input.addEventListener('blur', () => { if (!input.value) startPlaceholder(); });
  $('[data-clear]', controls).addEventListener('click', () => { input.value = ''; onName('clear'); input.focus(); });
  /* "More": the full set of symbols opens under the row, and stays open while you pick */
  const moreBtn = $('[data-sym-more]', controls), grid = $('[data-symgrid]', controls);
  const setGrid = (open) => { grid.hidden = !open; moreBtn.setAttribute('aria-expanded', String(open)); moreBtn.classList.toggle('is-open', open); };
  moreBtn.addEventListener('click', () => { setGrid(grid.hidden); if (!grid.hidden) log.count('symbols_more'); });
  grid.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setGrid(false); moreBtn.focus(); } });
  const touch = window.matchMedia('(pointer: coarse)').matches;
  controls.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sym]');
    if (!b) return;
    const ch = b.dataset.sym;
    /* at the caret if the field was being edited, else at the end */
    const editing = document.activeElement === input || input.dataset.caret !== undefined;
    const s = editing ? (input.selectionStart ?? input.value.length) : input.value.length;
    const en = editing ? (input.selectionEnd ?? s) : s;
    if (GEN.countChars(input.value) >= maxChars) { msg.textContent = t('err_limit', { max: maxChars }); msg.classList.add('is-error'); return; }
    input.setRangeText(ch, s, en, 'end');
    log.track('symbol', { ch, id: GEN.symbolFor(ch), via: b.closest('[data-symgrid]') ? 'more' : 'chip' });
    onName('chip');
    b.classList.remove('is-added'); void b.offsetWidth; b.classList.add('is-added');
    /* on a phone, focusing the field would pop the keyboard up over the preview on every tap */
    if (!touch) input.focus({ preventScroll: true });
  });
  input.addEventListener('blur', () => { input.dataset.caret = String(input.selectionStart ?? ''); });

  /* rotating example names in the empty field */
  const examples = String(ar && T.examples === EN.examples ? EXAMPLES_AR : T.examples).split('|').filter(Boolean);
  let phTimer = null, phIdx = 0;
  function startPlaceholder() {
    stopPlaceholder();
    input.placeholder = examples[0] || '';
    if (reduced() || examples.length < 2) return;
    phTimer = setInterval(() => { phIdx = (phIdx + 1) % examples.length; input.placeholder = examples[phIdx]; }, 2200);
  }
  function stopPlaceholder() { clearInterval(phTimer); phTimer = null; }
  if (!input.value) startPlaceholder();

  controls.addEventListener('change', (e) => {
    const i = e.target;
    /* each style has the case it looks best in, until the customer picks one themselves */
    if (i.name?.startsWith('nh-style')) set({ style: i.value, ...(caseTouched ? {} : { case: GEN.styleOf(i.value).case }) });
    else if (i.name?.startsWith('nh-layout')) set({ layout: i.value });
    else if (i.name?.startsWith('nh-case')) { caseTouched = true; set({ case: i.value }); }
    else if (i.name?.startsWith('nh-lines')) set({ lines: i.value });
    else if (i.name?.startsWith('nh-tone')) { set({ tone: i.value }); if (i.value !== 'one') { slot = 'b'; syncControls(); } }
    else if (i.name?.startsWith('nh-pockets')) set({ pockets: i.value });
    else if (i.name?.startsWith('nh-kind')) set({ kind: i.value });
    else if (i.name?.startsWith('nh-hang')) set({ hang: i.value });
    else if (i.name?.startsWith('nh-color')) set(slot === 'b' ? { color2: i.value } : { color: i.value }, 'swatch');
  });
  controls.addEventListener('click', (e) => {
    const s = e.target.closest('[data-slot]');
    if (s) { slot = s.dataset.slot; syncControls(); log.count('slot_switch'); return; }
    const d = e.target.closest('[data-duo]');
    if (d) { const duo = duos[Number(d.dataset.duo)]; slot = 'a'; log.track('duo', { a: duo.a.id, b: duo.b.id }); set({ tone: params.tone === 'one' ? (params.kind === 'sign' ? 'two' : 'kit') : params.tone, color: duo.a.id, color2: duo.b.id }, 'duo'); }
  });

  /* the dice: a whole new look in one tap */
  function surprise() {
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const style = pick(GEN.STYLES.filter((s) => s.id !== params.style));
    const layouts = style.connected ? ['straight', 'wave', 'arch'] : GEN.LAYOUTS;
    const useDuo = duos.length && Math.random() < 0.45;
    const duo = useDuo && pick(duos);
    const patch = {
      style: style.id, case: style.case, layout: pick(layouts), pockets: pick(GEN.POCKET_MODES),
      ...(duo ? { tone: params.kind === 'sign' ? 'two' : 'kit', color: duo.a.id, color2: duo.b.id } : { tone: 'one', color: pick(colors).id }),
    };
    if (!params.text) { const ex = pick(examples); input.value = ex; rawText = ex; patch.text = ex; }
    log.track('surprise', patch);
    log.count('surprise');
    set(patch, 'surprise');
  }
  $('[data-surprise]', tools).addEventListener('click', surprise);
  $('[data-dims]', tools).addEventListener('click', (e) => {
    const b = e.currentTarget, on = b.getAttribute('aria-pressed') !== 'true';
    b.setAttribute('aria-pressed', String(on)); stage?.showDims(on); log.track('view', { action: 'dims', on });
  });
  $('[data-pens]', tools).addEventListener('click', () => { pens = !pens; stage?.setPens(pens); syncControls(); log.track('view', { action: 'pens', on: pens }); });

  /* ---------- cart ---------- */
  function designUrl(share = false) {
    const u = new URL(product.url || location.pathname, location.origin);
    u.searchParams.set('d', GEN.encodeDesign(params));
    if (share) u.searchParams.set('nh', 'share');
    return u.toString();
  }
  function colorsLabel() {
    return params.tone !== 'one' ? `${cname(colorOf(params.color))} (${t(slotA())}) + ${cname(colorOf(params.color2))} (${t(slotB())})` : cname(colorOf(params.color));
  }
  /* a small studio shot of the design, as a JPEG; nothing when 3D is not up */
  async function cartThumb() {
    try {
      const url = stage?.snapshot?.({ size: 480, zoom: 0.74 });
      if (!url) return null;
      const img = new Image(); img.src = url; await img.decode();
      const c = document.createElement('canvas'); c.width = c.height = 360;
      c.getContext('2d').drawImage(img, 0, 0, 360, 360);
      return await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.86));
    } catch { return null; }
  }
  /* the STL for the order: same code as the maker's download, so what was ordered is what prints */
  const slugOf = () => GEN.applyCase(params.text, params.case).replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'name';
  async function printFiles() {
    try {
      const THREE = stage?.THREE || (await import('three'));
      const built = GEN.buildGeometries(THREE, plan, { smooth: false });
      if (!built) return {};
      const g = GEN.printFrame(THREE, plan, built);   // a sign goes on the bed on its back
      const head = `stacks ${GEN.encodeDesign(params)}`;
      const file = (parts, part) => new File([GEN.toSTL(parts, head)], `stacks-${slugOf()}-${part}.stl`, { type: 'model/stl' });
      /* a two-colour holder is one part with one filament change (plan.split); a sign is two parts */
      if (params.tone === 'kit') return { _stl_body: file([g.body], 'holder'), _stl_letters: file([g.letters], 'letters-x2') };
      return params.tone === 'two' && !plan.split
        ? { _stl_base: file([g.body], 'base'), _stl_letters: file([g.letters, g.steps], 'letters') }
        : { _stl: file([g.body, g.letters, g.steps], 'whole') };
    } catch (e) { log.error(e, 'stl'); return {}; }
  }
  async function addToCart(button, { buyNow = false } = {}) {
    /* a keystroke may still be waiting to rebuild: price and size must be the name as typed now */
    if (rebuildTimer) { clearTimeout(rebuildTimer); rebuildTimer = null; await rebuild(); }
    const p = problem();
    if (p || !plan) {
      msg.textContent = p?.text || t('loading'); msg.classList.add('is-error');
      /* the message sits by the name field — bring it into view (the phone bar is far from it) */
      const card = $('.nh-card--name', controls);
      card.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      card.classList.remove('is-shake'); void card.offsetWidth; card.classList.add('is-shake');
      if (p?.code === 'empty') setTimeout(() => input.focus({ preventScroll: true }), 350);
      log.track('atc_blocked', { why: p?.code || 'loading' });
      return;
    }
    const v = variant();
    const j = log.summary();
    const style = GEN.styleOf(params.style);
    const snapshot = { ...dimsNow(), tier: plan.tier, price: v.price, variant: v.id, w: plan.size.w, d: plan.size.d, h: plan.size.h, g: plan.grams, cap: plan.cap, pens, buy_now: buyNow ? 1 : 0 };
    log.track(buyNow ? 'buy_now' : 'atc_click', { ...snapshot, journey: j }, { now: true });
    const fd = new FormData();
    fd.append('id', v.id);
    fd.append('quantity', '1');
    fd.append(`properties[${t('prop_kind')}]`, t(`kind_${params.kind}`));
    fd.append(`properties[${t('prop_name')}]`, GEN.applyCase(params.text, params.case));
    if (params.kind === 'sign') fd.append(`properties[${t('prop_hang')}]`, `${t(`hang_${plan.hang.kind}`)} — ${hangText()}`);
    fd.append(`properties[${t('prop_style')}]`, `${sname(style)} · ${t(`layout_${params.layout}`)}`);
    fd.append(`properties[${t('prop_colors')}]`, colorsLabel());
    fd.append(`properties[${t('prop_design')}]`, designUrl());
    fd.append('properties[_nh]', GEN.encodeDesign(params));
    fd.append('properties[_nh_spec]', `${plan.size.w}x${plan.size.d}x${plan.size.h}mm ${plan.grams}g cap${plan.cap} ${plan.pockets.map((c) => c.kind).join('')}`);
    if (plan.split) fd.append('properties[_nh_change]', `Z ${plan.split.toFixed(1)} mm: ${colorOf(params.color2).id} → ${colorOf(params.color).id}`);
    fd.append('properties[_nh_sid]', log.sid);
    fd.append('properties[_nh_j]', Object.entries(j).map(([k, val]) => `${k}=${val}`).join(';'));
    button.classList.add('is-busy');
    /* the cart shows this design, not the product photo — and the order carries the print file
       itself, built from the very part on screen: one STL, or base + letters for two colours */
    const thumb = await cartThumb();
    if (thumb) fd.append('properties[_thumb]', thumb, 'preview.jpg');
    const files = await printFiles();
    for (const [key, file] of Object.entries(files)) fd.append(`properties[${key}]`, file, file.name);
    const t0 = performance.now();
    try {
      if (buyNow || typeof window.addToCart !== 'function') {
        const res = await fetch(`${window.Stacks?.routes?.cartAdd || '/cart/add'}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || `cart ${res.status}`);
      } else {
        await window.addToCart(fd, { button });
      }
      fetch(`${window.Stacks?.routes?.cartUpdate || '/cart/update'}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { _nh_sid: log.sid, _nh_vid: log.vid } }) }).catch(() => {});
      log.track('atc_ok', { ms: Math.round(performance.now() - t0), variant: v.id }, { now: true });
      celebrate();
      if (buyNow) { log.flush(true); location.href = '/checkout'; return; }
      if (typeof window.addToCart !== 'function') window.refreshCartUI?.({ open: true });
      button.classList.remove('is-busy');
      /* the button itself says it worked, for a moment, then goes back to the price */
      for (const b of [addBtn, dockAdd]) { b.dataset.done = '1'; b.textContent = `✓ ${t('added')}`; }
      setTimeout(() => { for (const b of [addBtn, dockAdd]) delete b.dataset.done; syncSummary(); }, 2600);
    } catch (e) {
      log.track('atc_fail', { msg: String(e.message || e).slice(0, 160), ms: Math.round(performance.now() - t0) }, { now: true });
      button.classList.remove('is-busy');
      syncSummary();
      msg.textContent = t('err_cart'); msg.classList.add('is-error');
    }
  }
  addBtn.addEventListener('click', () => addToCart(addBtn));
  buyBtn.addEventListener('click', () => addToCart(buyBtn, { buyNow: true }));
  dockAdd.addEventListener('click', () => addToCart(dockAdd));

  /* a small burst of layer-coloured bars — the one flourish on the page */
  function celebrate() {
    if (reduced()) return;
    const box = document.createElement('div');
    box.className = 'nh-burst'; box.setAttribute('aria-hidden', 'true');
    const hexes = [colorOf(params.color).hex, colorOf(params.tone !== 'one' ? params.color2 : params.color).hex, '#D2F24B', '#0F1013'];
    for (let i = 0; i < 26; i++) {
      const s = document.createElement('i');
      s.style.cssText = `--x:${(Math.random() - 0.5) * 360}px;--y:${-80 - Math.random() * 220}px;--r:${(Math.random() - 0.5) * 540}deg;--d:${0.55 + Math.random() * 0.5}s;background:${hexes[i % hexes.length]}`;
      box.appendChild(s);
    }
    (addBtn.closest('[data-buy]') || root).appendChild(box);
    setTimeout(() => box.remove(), 1400);
  }

  /* share: the phone's share sheet, else the clipboard */
  $('[data-share]', controls).addEventListener('click', async () => {
    const url = designUrl(true);
    let method = 'copy', ok = true;
    try {
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) { method = 'native'; await navigator.share({ title: document.title, text: params.text, url }); }
      else {
        await navigator.clipboard.writeText(url);
        /* say so where the eye is: the theme's toast, and a tick on the button itself */
        if (typeof window.toast === 'function') window.toast(esc(t('copied'))); else { msg.textContent = t('copied'); msg.classList.remove('is-error'); }
        const sb = $('[data-share]', controls);
        sb.classList.add('is-done'); setTimeout(() => sb.classList.remove('is-done'), 1800);
      }
    } catch (e) { ok = false; if (e?.name !== 'AbortError') method += '_failed'; }
    log.track('share', { method, ok: ok ? 1 : 0 });
  });

  /* the dock shows while the full buy card is off screen — and steps aside while a phone's
     keyboard is up, so it never rides on top of it */
  const buyCard = $('[data-buy]', controls);
  let typing = false, buySeen = false;
  syncDock = () => { dock.classList.toggle('is-on', !buySeen && !(typing && coarse)); };
  new IntersectionObserver(([e]) => { buySeen = e.isIntersecting; syncDock(); }, { threshold: 0.2 }).observe(buyCard);
  input.addEventListener('focus', () => { typing = true; syncDock(); });
  input.addEventListener('blur', () => { typing = false; syncDock(); });
  /* while the studio is on screen the theme's floating buttons (WhatsApp, the header CTA)
     step aside — the same class the product page uses for its buy row */
  new IntersectionObserver(([e]) => document.body.classList.toggle('buy-in-view', e.isIntersecting), { threshold: 0.15 }).observe(root);
  /* the stage sits just under the header wherever the header really is — it may stick, hide
     on the way down, or (as in this theme's section wrapper) simply scroll away */
  const hdr = document.querySelector('[data-header]');
  const phone = window.matchMedia('(max-width: 989px)');
  let topFrame = 0;
  const syncTop = () => {
    topFrame = 0;
    const below = hdr ? Math.max(0, Math.round(hdr.getBoundingClientRect().bottom)) : 0;
    root.style.setProperty('--nh-top', `${below + (phone.matches ? 0 : 12)}px`);
  };
  const queueTop = () => { if (!topFrame) topFrame = requestAnimationFrame(syncTop); };
  window.addEventListener('scroll', queueTop, { passive: true });
  window.addEventListener('resize', queueTop, { passive: true });
  if (hdr) { new MutationObserver(queueTop).observe(hdr, { attributes: true, attributeFilter: ['class'] }); hdr.addEventListener('transitionend', queueTop); }
  phone.addEventListener?.('change', queueTop);
  syncTop();

  /* ---------- the stage: flat preview first, 3D when it is ready ---------- */
  function setStage(state, text = '') {
    stageState = state;
    stageEl.dataset.state = state;
    const note = $('[data-nh-note]', stageEl);
    if (note) {
      note.innerHTML = state === 'error' ? `<span>${esc(text)}</span> <button type="button" class="btn btn--sm" data-retry>${esc(t('retry'))}</button>`
        : state === 'flat' ? `<span>${esc(text)}</span>` : state === 'loading' ? `<span class="nh-dots">${esc(t('loading'))}</span>` : '';
      $('[data-retry]', note)?.addEventListener('click', () => { setStage('loading'); log.track('retry', {}); schedule(0); if (!stage) start3d(); });
    }
  }
  setStage('loading');
  const bootT0 = performance.now();
  let started = false;
  async function start3d() {
    if (started && stage) return;
    started = true;
    const tThree = performance.now();
    try {
      stage = await createStage(stageEl, { onWheelPass, onInteract: (a) => { log.count(a); log.begin(); hint?.classList.add('is-gone'); } });
      root.nhStage = stage;   // for the harness and for debugging in the console
      /* the harness drives the studio through this (product photos, tests) */
      root.nhApi = {
        set: (patch) => { if ('text' in patch) { input.value = patch.text; rawText = patch.text; } set(patch, 'api'); },
        rebuild: () => rebuild(), pens: (on) => { pens = on; stage.setPens(on); syncControls(); }, plan: () => plan,
      };
      stage.setPens(pens);
      if (sceneNow !== 'studio') stage.setScene(sceneNow, lookNow);
      const threeMs = Math.round(performance.now() - tThree);
      await rebuild();
      if (!fontsFailed) log.track('ready', { ms: Math.round(performance.now() - bootT0), three_ms: threeMs, webgl: 1, gpu: stage.gpu, origin });
    } catch (e) {
      started = false;
      const webgl = e.message === 'webgl';
      log.track('ready', { ms: Math.round(performance.now() - bootT0), webgl: 0, fail: webgl ? 'webgl' : 'load', msg: String(e.message).slice(0, 120), origin });
      if (!webgl) log.error(e, 'stage');
      setStage(webgl ? 'flat' : 'error', webgl ? t('webgl_off') : t('err_fonts'));
      rebuild();   // still plan it: price, size and the cart work without the picture
    }
  }
  /* three.js is ~600 kB: fetch it when the studio is about to be seen */
  new IntersectionObserver((entries, io) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start3d(); } }, { rootMargin: '300px' }).observe(stageEl);

  const warmStyle = (id) => cssFont(GEN.styleOf(id));
  warmStyle(params.style).then(syncFlat);
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
  idle(() => GEN.STYLES.forEach((s) => warmStyle(s.id)));

  log.dims(dimsNow());
  if (fromLink) log.track('link_open', { origin, design: dimsNow() });
  if (origin === 'draft') log.track('draft_restore', { len: GEN.countChars(params.text) });
  syncControls(); syncFlat(); syncSamples(); syncSummary();
  schedule(0);

  /* ---------- maker mode: ?maker=<key> from the theme settings ---------- */
  if (makerAuthorized) {
    const box = document.createElement('div');
    box.className = 'nh-card nh-maker';
    box.innerHTML = `
      <div class="nh-card__head"><span class="nh-step">MK</span><span class="nh-card__title">${esc(t('maker_title'))}</span></div>
      <p class="nh-spec num" data-mk-spec></p>
      <div class="nh-maker__row">
        <input class="nh-name__input nh-maker__code" data-mk-code aria-label="${esc(t('maker_code'))}" placeholder="1.xxxx">
        <button type="button" class="btn btn--sm btn--ghost" data-mk-load>${esc(t('maker_load'))}</button>
      </div>
      <div class="nh-maker__row">
        <button type="button" class="btn btn--sm" data-mk="all">${esc(t('maker_all'))}</button>
        <button type="button" class="btn btn--sm btn--ghost" data-mk="body">${esc(t('maker_body'))}</button>
        <button type="button" class="btn btn--sm btn--ghost" data-mk="letters">${esc(t('maker_letters'))}</button>
      </div>
      <p class="nh-hint" data-mk-notes></p>`;
    controls.prepend(box);
    const code = $('[data-mk-code]', box);
    const refresh = () => {
      code.value = GEN.encodeDesign(params);
      $('[data-mk-notes]',box).textContent=t(params.construction==='organizer'&&params.v===GEN.GEN_VERSION?'solid_maker_notes':'maker_notes');
      $('[data-mk-spec]', box).textContent = plan && !plan.empty ? `${plan.size.w} × ${plan.size.d} × ${plan.size.h} mm · cap ${plan.cap} mm · ${plan.grams} g · cups ${plan.pockets.map((c) => c.kind).join(' ')}` : '';
    };
    setInterval(refresh, 800); refresh();
    $('[data-mk-load]', box).addEventListener('click', () => {
      const d = GEN.decodeDesign(code.value);
      if (!d) return;
      input.value = d.text || ''; rawText = input.value;
      set({ ...d }, 'maker');
    });
    box.addEventListener('click', async (e) => {
      const b = e.target.closest('[data-mk]');
      if (!b || !plan || plan.empty || plan.ghost) return;
      const THREE = stage?.THREE || (await import('three'));
      const geos = GEN.printFrame(THREE, plan, GEN.buildGeometries(THREE, plan));
      const parts = b.dataset.mk === 'body' ? [geos.body] : b.dataset.mk === 'letters' ? [geos.letters, geos.steps] : [geos.body, geos.letters, geos.steps];
      download(GEN.toSTL(parts, `stacks ${GEN.encodeDesign(params)}`), `stacks-${slugOf()}-${b.dataset.mk}.stl`);
      log.track('maker', { action: b.dataset.mk });
    });
  }
}

$$('[data-nh]').forEach((root) => boot(root).catch((e) => console.error('name holder:', e)));
